"use client";

import { ArrowDown, ArrowUp, Bookmark, ChevronLeft, Copy, FileText, Languages, Loader2, RefreshCw, Save, Search, X } from "lucide-react";
import { type MouseEvent, type ReactNode, type UIEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

type TropeType = 1 | 2 | 3;
type TropeMobileStage = "list" | "detail";
type TropeHistoryMode = "push" | "replace" | "none";
type TropeContentLanguage = "en" | "ko";
type TropeContentColumnMode = "content" | "markdown";
type TropeRoutePane = { slug: string; language?: TropeContentLanguage };
type TropeRoute = { type: TropeType; slug: string; paneSlugs: string[]; paneLanguages: TropeContentLanguage[]; explicit: boolean; explicitLanguages: boolean };
type TropeListMode = "reset" | "append" | "prepend" | "anchor";
type TropeListMeta = { total: number; offset: number; prevOffset: number; nextOffset: number; hasBefore: boolean; hasMore: boolean };

type TropeListItem = {
  id: number;
  slug: string;
  title: string | null;
  titleKo?: string | null;
  type: number;
  state: number;
  stateKo?: number;
  hasIssue: number;
  pinned: number;
  contentChars: number;
  updatedTs: number;
};

type TropeAncestorTag = {
  id: number;
  slug: string;
  title: string | null;
  titleKo?: string | null;
  type: number;
  depth: number;
};

type TropeItem = TropeListItem & {
  content: string;
  contentKo?: string;
  edgeDebug?: Record<string, { type: number }>;
  ancestorTags?: TropeAncestorTag[];
};

type TropeContentColumn = {
  mode?: TropeContentColumnMode;
  slug: string;
  language: TropeContentLanguage;
  item: TropeItem | null;
  loading: boolean;
  error: string;
  markdownDraft?: string;
  sourceSlug?: string;
};

type TropeInlineEdgeControls = {
  canAssignEdges: boolean;
  savingEdgeSlug: string;
  onAssignEdge: (slug: string) => void;
  onRemoveEdge: (slug: string) => void;
  onSetEdgeType: (slug: string, type: TropeType) => void;
};

type TropeInternalLinkHandler = (slug: string, event?: MouseEvent<HTMLButtonElement>) => void;
type TropeItemFetchMode = "content" | "relations" | "full";

const tabs: Array<{ type: TropeType; label: string }> = [
  { type: 2, label: "index" },
  { type: 1, label: "trope" },
  { type: 3, label: "media" }
];
const LIST_PAGE_SIZE = 120;
const ANCHOR_BEFORE_COUNT = 60;
const ANCHOR_AFTER_COUNT = 59;
const TROPE_CONTENT_LANGUAGE_STORAGE_KEY = "story37.trope.contentLanguage";

function emptyTropeListMeta(total = 0): TropeListMeta {
  return {
    total,
    offset: 0,
    prevOffset: 0,
    nextOffset: total,
    hasBefore: false,
    hasMore: false
  };
}

function storedTropeContentLanguage(): TropeContentLanguage {
  if (typeof window === "undefined") {
    return "en";
  }
  try {
    return window.localStorage.getItem(TROPE_CONTENT_LANGUAGE_STORAGE_KEY) === "ko" ? "ko" : "en";
  } catch {
    return "en";
  }
}

function persistTropeContentLanguage(language: TropeContentLanguage) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(TROPE_CONTENT_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Storage can be blocked in private or restricted browser modes.
  }
}

function getWindowScrollMetrics() {
  if (typeof window === "undefined") {
    return { scrollTop: 0, scrollHeight: 0, clientHeight: 0 };
  }
  const doc = document.documentElement;
  const body = document.body;
  return {
    scrollTop: window.scrollY || doc.scrollTop || body.scrollTop || 0,
    scrollHeight: Math.max(doc.scrollHeight, body.scrollHeight),
    clientHeight: window.innerHeight || doc.clientHeight
  };
}

function labelFor(item: Pick<TropeListItem, "slug" | "title">) {
  return item.title || item.slug;
}

function translatedLabelFor(item: Pick<TropeListItem, "slug" | "title" | "titleKo">, language: TropeContentLanguage) {
  if (language === "ko" && item.titleKo && item.titleKo.trim()) {
    return item.titleKo;
  }
  return item.title || item.slug;
}

function contentFor(item: TropeItem | null, language: TropeContentLanguage) {
  if (!item) {
    return "";
  }
  if (language === "ko" && item.contentKo && item.contentKo.trim()) {
    return item.contentKo;
  }
  return item.content || "";
}

function oppositeLanguage(language: TropeContentLanguage): TropeContentLanguage {
  return language === "en" ? "ko" : "en";
}

function languageLabel(language: TropeContentLanguage) {
  return language === "en" ? "English" : "Korean";
}

function normalizeContentLanguage(value: string | null | undefined): TropeContentLanguage | null {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "ko" || normalized === "kr" || normalized === "korean" || normalized === "한글") {
    return "ko";
  }
  if (normalized === "en" || normalized === "eng" || normalized === "english") {
    return "en";
  }
  return null;
}

async function fetchTropeItemPayload(
  slug: string,
  options: { sourceSlug?: string; language: TropeContentLanguage; mode: TropeItemFetchMode }
) {
  const params = new URLSearchParams({ slug, mode: options.mode, language: options.language });
  if (options.sourceSlug && options.sourceSlug !== slug) {
    params.set("sourceSlug", options.sourceSlug);
  }
  const response = await fetch(`/api/plot-tropes/browse/item?${params.toString()}`, {
    cache: "no-store"
  });
  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }
  return payload as {
    item: TropeItem;
    result?: {
      sourceSlug?: string;
      sourceContent?: string;
    };
  };
}

function sortTropeListItems(items: TropeListItem[]) {
  return [...items].sort((left, right) => {
    const pinnedDelta = Number(right.pinned || 0) - Number(left.pinned || 0);
    if (pinnedDelta) {
      return pinnedDelta;
    }
    const slugDelta = String(left.slug || "").localeCompare(String(right.slug || ""));
    if (slugDelta) {
      return slugDelta;
    }
    return Number(left.id || 0) - Number(right.id || 0);
  });
}

const indexTagPrefixes = [
  "An Index of ",
  "Indexes of ",
  "This Index ",
  "Index of ",
  "Tropes That ",
  "Tropes of ",
  "Tropes in ",
  "Tropes on ",
  "Tropes ",
  "Index "
].sort((a, b) => b.length - a.length);
const indexTagSuffixes = [
  "에 관한 색인",
  " 트로프 모음",
  " 관련 클리셰",
  " 관련 트로프",
  " 관련 색인",
  " 관련 트롭",
  " 트롭 모음",
  " 이 색인",
  "의 목록",
  " Indexes",
  " Tropes",
  " 인덱스",
  " 트로프",
  " 클리셰",
  " Trope",
  " Index",
  " 트롭",
  " 색인",
  " 목록",
  " 모음"
].sort((a, b) => b.length - a.length);

function stripIndexTagTitle(value: string) {
  const original = String(value || "").trim();
  let text = original.replace(/\s+/g, " ");
  for (const prefix of indexTagPrefixes) {
    if (text.toLowerCase().startsWith(prefix.toLowerCase())) {
      text = text.slice(prefix.length).trim();
      break;
    }
  }

  text = text
    .replace(/\s*,\s*Index([!?])?$/i, (_match, punctuation = "") => punctuation)
    .replace(/\s+of\s+the\s+Index([!?])?$/i, (_match, punctuation = "") => punctuation)
    .trim();

  let strippedSingularSuffix = false;
  for (const suffix of indexTagSuffixes) {
    if (text.toLowerCase().endsWith(suffix.toLowerCase())) {
      strippedSingularSuffix = suffix === " Trope" || suffix === " Index";
      text = text.slice(0, -suffix.length).trim();
      break;
    }
  }

  if (strippedSingularSuffix) {
    text = text.replace(/^(an|a)\s+/i, "").trim();
  }

  return text || original;
}

function isTextEditingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tagName = target.tagName.toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select" || target.isContentEditable || Boolean(target.closest("[contenteditable='true']"));
}

function tropeTypeParam(type: TropeType) {
  if (type === 3) {
    return "media";
  }
  return type === 2 ? "index" : "trope";
}

function tropeTypeFromParam(value: string | null): TropeType {
  if (value === "3" || value === "media") {
    return 3;
  }
  return value === "2" || value === "index" ? 2 : 1;
}

function normalizeTropeType(value: unknown): TropeType {
  const parsed = Number(value);
  if (parsed === 2 || parsed === 3) {
    return parsed;
  }
  return 1;
}

function tropeTypeLabel(type: number) {
  if (type === 3) {
    return "media";
  }
  return type === 2 ? "index" : "trope";
}

function tropeTypeGlyph(type: number) {
  if (type === 3) {
    return "🐥";
  }
  if (type === 2) {
    return "🦑";
  }
  return "🦋";
}

function switchableTropeTypeOptions(type: number): TropeType[] {
  const current = normalizeTropeType(type);
  if (current === 3) {
    return [2, 1];
  }
  if (current === 2) {
    return [3, 1];
  }
  return [3, 2];
}

function cleanTropeSlug(slug: string) {
  const raw = slug.trim();
  if (!raw) {
    return "";
  }
  let clean = raw;
  try {
    const url = new URL(raw);
    const marker = "/pmwiki/pmwiki.php/";
    const markerIndex = url.pathname.indexOf(marker);
    if (markerIndex >= 0) {
      clean = url.pathname.slice(markerIndex + marker.length);
    }
  } catch {
    clean = raw;
  }
  clean = clean.replace(/^\/+/, "").replace(/^pmwiki\/pmwiki\.php\//i, "").split(/[?#]/)[0];
  try {
    clean = decodeURIComponent(clean);
  } catch {
    // Keep the undecoded slug when it contains a malformed escape sequence.
  }
  clean = clean.split("/").filter(Boolean).join("/");
  if (!clean) {
    return "";
  }
  return clean.includes("/") ? clean : `Main/${clean}`;
}

function looksLikeTropeSlugSearch(value: string) {
  const text = String(value || "").trim();
  if (!text) {
    return false;
  }
  return /^https?:\/\/(?:www\.)?tvtropes\.org\/pmwiki\/pmwiki\.php\//i.test(text) || /^[A-Za-z][A-Za-z0-9]*\/[A-Za-z0-9][A-Za-z0-9_.-]*$/.test(text);
}

function normalizeRoutePanes(slug = "", panes: TropeRoutePane[] = [], fallbackLanguage: TropeContentLanguage = "en") {
  const normalized: TropeRoutePane[] = [];
  const firstSlug = cleanTropeSlug(slug);
  if (firstSlug) {
    normalized.push({
      slug: firstSlug,
      language: panes[0]?.language || fallbackLanguage
    });
  }
  for (const pane of panes) {
    const paneSlug = cleanTropeSlug(pane.slug);
    if (!paneSlug || (normalized.length === 1 && paneSlug === firstSlug)) {
      continue;
    }
    normalized.push({
      slug: paneSlug,
      language: pane.language || fallbackLanguage
    });
  }
  return normalized;
}

function tropeRouteUrl(type: TropeType, slug = "", panes: TropeRoutePane[] = []) {
  const params = new URLSearchParams();
  params.set("type", tropeTypeParam(type));
  const normalizedPanes = normalizeRoutePanes(slug, panes, panes[0]?.language || "en");
  if (normalizedPanes.length > 0) {
    params.set("slug", normalizedPanes.map((pane) => pane.slug).join(","));
    params.set("lang", normalizedPanes.map((pane) => pane.language || "en").join(","));
  }
  return `/trope?${params.toString()}`;
}

function tropeRouteState(type: TropeType, slug = "", panes: TropeRoutePane[] = []) {
  const normalizedPanes = normalizeRoutePanes(slug, panes, panes[0]?.language || "en");
  return {
    story37TropeRoute: {
      type,
      slug,
      paneSlugs: normalizedPanes.map((pane) => pane.slug),
      paneLanguages: normalizedPanes.map((pane) => pane.language || "en")
    }
  };
}

function tropeSourceUrl(slug: string) {
  const cleanSlug = cleanTropeSlug(slug);
  const path = cleanSlug
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join("/");
  return `https://tvtropes.org/pmwiki/pmwiki.php/${path}`;
}

function currentTropeRoute(): TropeRoute {
  if (typeof window === "undefined") {
    return { type: 1, slug: "", paneSlugs: [], paneLanguages: [], explicit: false, explicitLanguages: false };
  }
  const params = new URLSearchParams(window.location.search);
  const indexSlug = params.get("index");
  const mediaSlug = params.get("media");
  const tropeSlug = params.get("trope");
  const type = mediaSlug ? 3 : indexSlug ? 2 : tropeSlug ? 1 : tropeTypeFromParam(params.get("type"));
  const explicitSlugValue = mediaSlug || indexSlug || tropeSlug || params.get("slug") || "";
  const paneSlugs = explicitSlugValue
    .split(",")
    .map((value) => cleanTropeSlug(value))
    .filter(Boolean);
  const explicitLanguages = params.has("lang");
  const rawLanguages = (params.get("lang") || "")
    .split(",")
    .map((value) => normalizeContentLanguage(value));
  const paneLanguages = paneSlugs.map((_, index) => rawLanguages[index] || rawLanguages[0] || "en");
  const slug = paneSlugs[0] || "";
  return {
    type,
    slug,
    paneSlugs,
    paneLanguages,
    explicit: params.has("type") || params.has("slug") || params.has("media") || params.has("index") || params.has("trope"),
    explicitLanguages
  };
}

function unescapeMarkdown(value: string) {
  return value.replace(/\\([\\`*_{}[\]()#+.!|>~-])/g, "$1");
}

function internalSlugFromHref(href: string) {
  const raw = href.trim();
  if (!raw) {
    return "";
  }
  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      if (url.hostname !== "tvtropes.org" || !url.pathname.includes("/pmwiki/pmwiki.php/")) {
        return "";
      }
    } catch {
      return "";
    }
  } else {
    const relative = raw.replace(/^\/+/, "");
    if (!relative.startsWith("pmwiki/pmwiki.php/") && !/^[^/?#]+\/[^/?#]+/.test(relative)) {
      return "";
    }
  }
  const clean = cleanTropeSlug(raw);
  if (!/^[^/]+\/[^/]+$/.test(clean)) {
    return "";
  }
  return clean;
}

function edgeDebugType(edgeDebug: Record<string, { type: number }> | undefined, slug: string) {
  const edge = edgeDebug?.[slug] || edgeDebug?.[slug.toLowerCase()];
  return edge ? Number(edge.type || 0) : 0;
}

function edgeDebugGlyph(type: number) {
  if (type === 1) {
    return "🦋";
  }
  if (type === 2) {
    return "🦑";
  }
  if (type === 3) {
    return "🐥";
  }
  return "";
}

function edgeTypeOptions(type: number): TropeType[] {
  return ([3, 2, 1] as TropeType[]).filter((option) => option !== type);
}

function mainSlugsFromListItem(item: string) {
  const links: Array<{ slug: string; index: number; endIndex: number }> = [];
  const tokenRe = /\[[^\]]+\]\(([^)]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = tokenRe.exec(item))) {
    if (match.index > 0 && item[match.index - 1] === "!") {
      continue;
    }
    const linkHref = match[1];
    if (!linkHref) {
      continue;
    }
    const slug = internalSlugFromHref(linkHref);
    if (slug.startsWith("Main/")) {
      links.push({ slug, index: match.index, endIndex: tokenRe.lastIndex });
    }
  }

  if (links.length <= 1) {
    if (links.length === 0) {
      return [];
    }
    const prefix = item.slice(0, links[0].index).replace(/[*_\s]+/g, "");
    return prefix ? [] : [links[0].slug];
  }

  const prefix = item.slice(0, links[0].index).replace(/[*_\s]+/g, "");
  if (prefix) {
    return [];
  }
  const slugs = [links[0].slug];
  for (let index = 1; index < links.length; index += 1) {
    const separator = item.slice(links[index - 1].endIndex, links[index].index).replace(/[\s*_]+/g, "");
    if (separator !== "/") {
      break;
    }
    slugs.push(links[index].slug);
  }
  return slugs;
}

function mainChildSlugsFromListItems(items: string[]) {
  const slugs = new Set<string>();
  for (const item of items) {
    for (const slug of mainSlugsFromListItem(item)) {
      slugs.add(slug);
    }
  }
  return [...slugs];
}

function markdownBulletIndentLevel(indent: string) {
  const columns = String(indent || "").replace(/\t/g, "    ").length;
  return Math.min(5, Math.max(0, Math.floor((columns + 1) / 2)));
}

function isMarkdownListBoundary(line: string) {
  const trimmed = String(line || "").trim();
  return !trimmed || /^(#{1,6})\s+/.test(trimmed) || /^---+$/.test(trimmed) || /^>\s?/.test(trimmed);
}

function isPlainListTitleLine(line: string) {
  const trimmed = String(line || "")
    .trim()
    .replace(/^\*\*(.+)\*\*$/, "$1")
    .trim();
  if (!trimmed || trimmed.length > 120) {
    return false;
  }
  if (trimmed.endsWith(":")) {
    return true;
  }
  return !/[.!?]$/.test(trimmed);
}

function normalizeFloatboxSmallTitle(lines: string[]) {
  const joined = lines
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return joined
    .replace(/^[*_]+/, "")
    .replace(/[*_]+$/, "")
    .replace(/^['"`]+/, "")
    .replace(/['"`]+$/, "")
    .trim();
}

function isFloatboxSmallTitle(lines: string[]) {
  const title = normalizeFloatboxSmallTitle(lines);
  if (!title || title.length > 180) {
    return false;
  }
  return true;
}

function isCompactFloatboxBullet(line: string) {
  if (!/^\s*-\s+/.test(line)) {
    return false;
  }
  let rest = String(line || "").replace(/^\s*-\s+/, "").trim();
  rest = rest.replace(/!\[[^\]]*\]\([^)]+\)/g, "");
  rest = rest.replace(/\[[^\]]+\]\([^)]+\)/g, "");
  rest = rest.replace(/[`*_~()[\],.;:\/&+|-]/g, "");
  rest = rest.replace(/\s+/g, "");
  return rest.length === 0;
}

function renderInline(
  text: string,
  onInternalLink: TropeInternalLinkHandler,
  keyPrefix: string,
  edgeDebug?: Record<string, { type: number }>,
  edgeControls?: TropeInlineEdgeControls,
  readOnlyEdgeDebug = false,
  highlightColonLabels = false
): ReactNode[] {
  const nodes: ReactNode[] = [];
  const source = String(text || "");
  const tokenRe = /!\[([^\]]*)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)|\*\*([\s\S]+?)\*\*|\*([^*\n]+?)\*/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  function pushText(value: string, key: string) {
    if (!value) {
      return;
    }
    const plainText = unescapeMarkdown(value);
    if (!highlightColonLabels) {
      nodes.push(<span key={key}>{plainText}</span>);
      return;
    }

    const labelRe = /(^|,)([^,:：\n]{1,48}?)([:：])/g;
    let labelCursor = 0;
    let labelIndex = 0;
    let labelMatch: RegExpExecArray | null;
    while ((labelMatch = labelRe.exec(plainText))) {
      const separator = labelMatch[1];
      const label = labelMatch[2];
      const colon = labelMatch[3];
      const labelStart = labelMatch.index + separator.length;
      const labelEnd = labelStart + label.length;
      const colonEnd = labelEnd + colon.length;
      const trimmedLabel = label.trim();
      const colonTail = plainText.slice(colonEnd, colonEnd + 2);
      if (!trimmedLabel || colonTail === "//" || /^https?$/i.test(trimmedLabel)) {
        continue;
      }
      if (labelStart > labelCursor) {
        nodes.push(<span key={`${key}-plain-${labelIndex}`}>{plainText.slice(labelCursor, labelStart)}</span>);
      }
      nodes.push(
        <span className="tropeMarkdownColonLabel" key={`${key}-label-${labelIndex}`}>
          {plainText.slice(labelStart, labelEnd)}
        </span>
      );
      labelCursor = labelEnd;
      labelIndex += 1;
    }

    if (labelCursor < plainText.length) {
      nodes.push(<span key={`${key}-plain-end`}>{plainText.slice(labelCursor)}</span>);
    }
  }

  function renderLabel(value: string, key: string) {
    return renderInline(value, onInternalLink, key, edgeDebug, edgeControls, readOnlyEdgeDebug, highlightColonLabels);
  }

  while ((match = tokenRe.exec(source))) {
    pushText(source.slice(cursor, match.index), `${keyPrefix}-t-${cursor}`);
    const imageAlt = match[1];
    const imageHref = match[2];
    const linkLabel = match[3];
    const linkHref = match[4];
    const boldText = match[5];
    const italicText = match[6];

    if (imageHref) {
      nodes.push(
        <img
          alt={unescapeMarkdown(imageAlt || "")}
          className="tropeMarkdownImage"
          key={`${keyPrefix}-img-${match.index}`}
          src={imageHref}
        />
      );
    } else if (linkHref) {
      const slug = internalSlugFromHref(linkHref);
      if (slug) {
        if (slug.startsWith("Main/")) {
          const debugType = edgeDebugType(edgeDebug, slug);
          const isSavingEdge = edgeControls?.savingEdgeSlug === slug;
          nodes.push(
            <span className={`tropeInlineEdgeWrap ${debugType ? "hasEdge" : ""}`} key={`${keyPrefix}-jump-wrap-${match.index}`}>
              <button
                className="tropeInlineJump"
                data-slug={slug}
                onClick={(event) => onInternalLink(slug, event)}
                type="button"
              >
                {renderLabel(linkLabel || slug, `${keyPrefix}-jump-label-${match.index}`)}
              </button>
              {debugType && readOnlyEdgeDebug ? (
                <span className="tropeEdgeDebugText" aria-label={tropeTypeLabel(debugType)} title={tropeTypeLabel(debugType)}>
                  {edgeDebugGlyph(debugType)}
                </span>
              ) : debugType ? (
                <span className="tropeEdgeDebugCluster">
                  <button
                    aria-label="Edge actions"
                    className="tropeEdgeDebugButton"
                    disabled={isSavingEdge}
                    title="edge actions"
                    type="button"
                  >
                    {edgeDebugGlyph(debugType)}
                  </button>
                  <span className="tropeEdgeDebugMenu">
                    <button
                      aria-label="Remove edge"
                      className="tropeEdgeDebugAction remove"
                      disabled={isSavingEdge}
                      onClick={() => edgeControls?.onRemoveEdge(slug)}
                      title="edge 해제"
                      type="button"
                    >
                      ❌
                    </button>
                    {edgeTypeOptions(debugType).map((typeOption) => (
                      <button
                        aria-label={`Set as ${tropeTypeLabel(typeOption)}`}
                        className="tropeEdgeDebugAction"
                        disabled={isSavingEdge}
                        key={typeOption}
                        onClick={() => edgeControls?.onSetEdgeType(slug, typeOption)}
                        title={`Set as ${tropeTypeLabel(typeOption)}`}
                        type="button"
                      >
                        {edgeDebugGlyph(typeOption)}
                      </button>
                    ))}
                  </span>
                </span>
              ) : edgeControls?.canAssignEdges ? (
                <button
                  className="tropeInlineEdgeAddButton"
                  disabled={isSavingEdge}
                  onClick={() => edgeControls.onAssignEdge(slug)}
                  title="Assign edge"
                  type="button"
                >
                  {isSavingEdge ? "..." : "edge"}
                </button>
              ) : null}
            </span>
          );
        } else {
          nodes.push(
            <span
              className="tropeInlineUnparsedSlug"
              data-slug={slug}
              key={`${keyPrefix}-unparsed-${match.index}`}
              title={slug}
            >
              {renderLabel(linkLabel || slug, `${keyPrefix}-unparsed-label-${match.index}`)}
            </span>
          );
        }
      } else {
        nodes.push(
          <a href={linkHref} key={`${keyPrefix}-a-${match.index}`} rel="noreferrer" target="_blank">
            {renderLabel(linkLabel || linkHref, `${keyPrefix}-a-label-${match.index}`)}
          </a>
        );
      }
    } else if (boldText) {
      nodes.push(<b key={`${keyPrefix}-b-${match.index}`}>{renderLabel(boldText, `${keyPrefix}-b-label-${match.index}`)}</b>);
    } else if (italicText) {
      nodes.push(<i key={`${keyPrefix}-i-${match.index}`}>{renderLabel(italicText, `${keyPrefix}-i-label-${match.index}`)}</i>);
    }

    cursor = tokenRe.lastIndex;
  }

  pushText(source.slice(cursor), `${keyPrefix}-t-end`);
  return nodes;
}

export function MarkdownView({
  markdown,
  onInternalLink,
  edgeDebug,
  canAssignEdges = false,
  forceMediaEdgeTools = false,
  savingEdgeListKey = "",
  savingEdgeSlug = "",
  onAssignEdge,
  onRemoveEdge,
  onSetEdgeType,
  onAssignEdgeList,
  onFixFloatbox,
  fixingFloatbox = false,
  readOnlyEdgeDebug = false,
  preserveParagraphLineBreaks = false
}: {
  markdown: string;
  onInternalLink: TropeInternalLinkHandler;
  edgeDebug?: Record<string, { type: number }>;
  canAssignEdges?: boolean;
  forceMediaEdgeTools?: boolean;
  savingEdgeListKey?: string;
  savingEdgeSlug?: string;
  onAssignEdge?: (slug: string) => void;
  onRemoveEdge?: (slug: string) => void;
  onSetEdgeType?: (slug: string, type: TropeType) => void;
  onAssignEdgeList?: (input: { childSlugs: string[]; listKey: string; forceChildMedia?: boolean }) => void;
  onFixFloatbox?: () => void;
  fixingFloatbox?: boolean;
  readOnlyEdgeDebug?: boolean;
  preserveParagraphLineBreaks?: boolean;
}) {
  const blocks = useMemo(() => {
    const lines = String(markdown || "").replace(/\r\n/g, "\n").split("\n");
    const result: ReactNode[] = [];
    const inlineEdgeControls =
      onAssignEdge && onRemoveEdge && onSetEdgeType
        ? {
            canAssignEdges,
            savingEdgeSlug,
            onAssignEdge,
            onRemoveEdge,
            onSetEdgeType
          }
        : undefined;
    let attachableTitle:
      | {
          lineIndex: number;
          resultIndex: number;
          kind: "heading" | "paragraph";
          render: (edgeButton: ReactNode) => ReactNode;
        }
      | null = null;
    let index = 0;

    function renderEdgeButton(childSlugs: string[], listKey: string) {
      if (!onAssignEdgeList) {
        return null;
      }
      return (
        <>
          <button
            className="tropeEdgeAssignButton"
            disabled={Boolean(savingEdgeListKey)}
            key={`edge-${listKey}`}
            onClick={() => onAssignEdgeList({ childSlugs, listKey })}
            title="Assign this list as edges"
            type="button"
          >
            {savingEdgeListKey === listKey ? "..." : "edge"}
          </button>
          {forceMediaEdgeTools ? (
            <button
              className="tropeEdgeAssignButton forceMedia"
              disabled={Boolean(savingEdgeListKey)}
              key={`media-edge-${listKey}`}
              onClick={() => onAssignEdgeList({ childSlugs, listKey, forceChildMedia: true })}
              title="Assign this list as edges and force children as media"
              type="button"
            >
              {savingEdgeListKey === `${listKey}:media` ? "..." : "🐥edge"}
            </button>
          ) : null}
        </>
      );
    }

    function renderFloatboxFixButton(key: string) {
      if (!onFixFloatbox) {
        return null;
      }
      return (
        <button
          className="tropeFloatboxFixButton"
          disabled={fixingFloatbox}
          key={key}
          onClick={onFixFloatbox}
          type="button"
        >
          {fixingFloatbox ? "fixing..." : "fix floatbox"}
        </button>
      );
    }

    function listStartsAt(lineIndex: number) {
      return lineIndex < lines.length && /^\s*-\s+/.test(lines[lineIndex]);
    }

    function firstNonBlankLine(lineIndex: number) {
      let cursor = lineIndex;
      while (cursor < lines.length && !lines[cursor].trim()) {
        cursor += 1;
      }
      return cursor;
    }

    function firstLineAfterList(lineIndex: number) {
      let cursor = lineIndex;
      while (cursor < lines.length && !isMarkdownListBoundary(lines[cursor])) {
        cursor += 1;
      }
      while (cursor < lines.length && !lines[cursor].trim()) {
        cursor += 1;
      }
      return cursor;
    }

    function isCompactListAt(lineIndex: number) {
      let cursor = firstNonBlankLine(lineIndex);
      let sawBullet = false;
      while (cursor < lines.length && lines[cursor].trim()) {
        if (/^\s*-\s+/.test(lines[cursor])) {
          if (!isCompactFloatboxBullet(lines[cursor])) {
            return false;
          }
          sawBullet = true;
          cursor += 1;
          continue;
        }
        if (!sawBullet || /^(#{1,6})\s+/.test(lines[cursor]) || /^---+$/.test(lines[cursor].trim())) {
          return false;
        }
        cursor += 1;
      }
      return sawBullet;
    }

    function hasMainListAfterSideList(lineIndex: number): boolean {
      const cursor = firstNonBlankLine(lineIndex);
      if (listStartsAt(cursor)) {
        return true;
      }
      return cursor >= lines.length || !/^(#{1,6})\s+/.test(lines[cursor]);
    }

    function isFloatboxFixCandidate(paragraphLines: string[], nextLineIndex: number) {
      const firstListIndex = firstNonBlankLine(nextLineIndex);
      return (
        Boolean(onFixFloatbox) &&
        attachableTitle?.kind === "heading" &&
        isFloatboxSmallTitle(paragraphLines) &&
        listStartsAt(firstListIndex) &&
        isCompactListAt(firstListIndex) &&
        hasMainListAfterSideList(firstLineAfterList(firstListIndex))
      );
    }

    function attachEdgeButtonToPreviousTitle(edgeButton: ReactNode) {
      if (!attachableTitle) {
        return false;
      }
      result[attachableTitle.resultIndex] = attachableTitle.render(edgeButton);
      attachableTitle = null;
      return true;
    }

    function renderParagraphInline(paragraphLines: string[], keyPrefix: string) {
      if (!preserveParagraphLineBreaks) {
        return renderInline(paragraphLines.join(" "), onInternalLink, keyPrefix, edgeDebug, inlineEdgeControls, readOnlyEdgeDebug, true);
      }
      const nodes: ReactNode[] = [];
      paragraphLines.forEach((paragraphLine, lineIndex) => {
        nodes.push(
          ...renderInline(paragraphLine.replace(/[ \t]+$/g, ""), onInternalLink, `${keyPrefix}-${lineIndex}`, edgeDebug, inlineEdgeControls, readOnlyEdgeDebug, true)
        );
        if (lineIndex < paragraphLines.length - 1) {
          nodes.push(<br key={`${keyPrefix}-br-${lineIndex}`} />);
        }
      });
      return nodes;
    }

    while (index < lines.length) {
      const line = lines[index];
      if (!line.trim()) {
        index += 1;
        continue;
      }

      const heading = /^(#{1,6})\s+(.+)$/.exec(line);
      if (heading) {
        const level = Math.min(6, heading[1].length);
        const lineIndex = index;
        const resultIndex = result.length;
        result.push(
          <div className={`tropeMdHeading h${level}`} key={`h-${index}`}>
            {renderInline(heading[2], onInternalLink, `h-${index}`, edgeDebug, inlineEdgeControls, readOnlyEdgeDebug)}
          </div>
        );
        attachableTitle = {
          lineIndex,
          resultIndex,
          kind: "heading",
          render: (edgeButton) => (
            <div className={`tropeMdHeading h${level} withEdgeButton`} key={`h-${lineIndex}`}>
              <span className="tropeTitleInlineText">
                {renderInline(heading[2], onInternalLink, `h-${lineIndex}`, edgeDebug, inlineEdgeControls, readOnlyEdgeDebug)}
              </span>
              {edgeButton}
            </div>
          )
        };
        index += 1;
        continue;
      }

      if (/^---+$/.test(line.trim())) {
        attachableTitle = null;
        result.push(<hr key={`hr-${index}`} />);
        index += 1;
        continue;
      }

      if (/^\s*>\s?/.test(line)) {
        attachableTitle = null;
        const quoteLines: string[] = [];
        const start = index;
        while (index < lines.length && /^\s*>\s?/.test(lines[index])) {
          quoteLines.push(lines[index].replace(/^\s*>\s?/, ""));
          index += 1;
        }
        result.push(
          <blockquote key={`q-${start}`}>
            {quoteLines.map((quoteLine, quoteIndex) => (
              <p key={`q-${start}-${quoteIndex}`}>
                {renderInline(quoteLine, onInternalLink, `q-${start}-${quoteIndex}`, edgeDebug, inlineEdgeControls, readOnlyEdgeDebug, true)}
              </p>
            ))}
          </blockquote>
        );
        continue;
      }

      if (/^\s*-\s+/.test(line)) {
        const entries: Array<{ item: string; detailLines: string[]; level: number }> = [];
        const start = index;
        while (index < lines.length && !isMarkdownListBoundary(lines[index])) {
          const bullet = /^(\s*)-\s+(.+)$/.exec(lines[index]);
          if (bullet) {
            entries.push({ item: bullet[2], detailLines: [], level: markdownBulletIndentLevel(bullet[1]) });
          } else if (entries.length > 0) {
            entries[entries.length - 1].detailLines.push(lines[index].trim());
          }
          index += 1;
        }
        const listKey = `ul-${start}`;
        const childSlugs = mainChildSlugsFromListItems(entries.map((entry) => entry.item));
        const showEdgeTools = canAssignEdges && childSlugs.length > 0 && onAssignEdgeList;
        const edgeButton = showEdgeTools ? renderEdgeButton(childSlugs, listKey) : null;
        const edgeButtonAttached = edgeButton ? attachEdgeButtonToPreviousTitle(edgeButton) : false;
        attachableTitle = null;
        result.push(
          <div className="tropeListEdgeBlock" key={`list-${start}`}>
            {edgeButton && !edgeButtonAttached ? (
              <div className="tropeListEdgeTools">
                {edgeButton}
              </div>
            ) : null}
            <ul className="tropeMarkdownList">
              {entries.map((entry, itemIndex) => (
                <li className={`tropeMarkdownListItem level${entry.level}`} key={`li-${start}-${itemIndex}`}>
                  {renderInline(entry.item, onInternalLink, `li-${start}-${itemIndex}`, edgeDebug, inlineEdgeControls, readOnlyEdgeDebug, true)}
                  {entry.detailLines.length > 0 ? (
                    <div className="tropeMarkdownListDetail">
                      {renderInline(entry.detailLines.join(" "), onInternalLink, `li-${start}-${itemIndex}-detail`, edgeDebug, inlineEdgeControls, readOnlyEdgeDebug, true)}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        );
        continue;
      }

      const paragraphLines = [line.trim()];
      const start = index;
      index += 1;
      while (
        index < lines.length &&
        lines[index].trim() &&
        !/^(#{1,6})\s+/.test(lines[index]) &&
        !/^---+$/.test(lines[index].trim()) &&
        !/^\s*>\s?/.test(lines[index]) &&
        !/^\s*-\s+/.test(lines[index])
      ) {
        paragraphLines.push(lines[index].trim());
        index += 1;
      }
      const isAttachablePlainTitle = paragraphLines.length === 1 && isPlainListTitleLine(paragraphLines[0]);
      const floatboxFixButton = isFloatboxFixCandidate(paragraphLines, index)
        ? renderFloatboxFixButton(`fix-floatbox-${start}`)
        : null;
      const resultIndex = result.length;
      result.push(
        <p className={floatboxFixButton ? "withFloatboxFixButton" : undefined} key={`p-${start}`}>
          <span className="tropeTitleInlineText">
            {renderParagraphInline(paragraphLines, `p-${start}`)}
          </span>
          {floatboxFixButton}
        </p>
      );
      attachableTitle = isAttachablePlainTitle
        ? {
            lineIndex: start,
            resultIndex,
            kind: "paragraph",
            render: (edgeButton) => (
              <p className={`withEdgeButton${floatboxFixButton ? " withFloatboxFixButton" : ""}`} key={`p-${start}`}>
                <span className="tropeTitleInlineText">
                  {renderParagraphInline([paragraphLines[0]], `p-${start}`)}
                </span>
                {floatboxFixButton}
                {edgeButton}
              </p>
            )
          }
        : null;
    }

    return result;
  }, [canAssignEdges, edgeDebug, fixingFloatbox, forceMediaEdgeTools, markdown, onAssignEdge, onAssignEdgeList, onFixFloatbox, onInternalLink, onRemoveEdge, onSetEdgeType, preserveParagraphLineBreaks, readOnlyEdgeDebug, savingEdgeListKey, savingEdgeSlug]);

  return <div className="tropeMarkdown">{blocks}</div>;
}

export function TropeBrowser() {
  const [activeType, setActiveType] = useState<TropeType>(1);
  const [lists, setLists] = useState<Record<number, TropeListItem[]>>({});
  const [listMeta, setListMeta] = useState<Record<number, TropeListMeta>>({});
  const [loadedTypes, setLoadedTypes] = useState<Record<number, boolean>>({});
  const [loadingListType, setLoadingListType] = useState<TropeType | null>(null);
  const [selected, setSelected] = useState<TropeItem | null>(null);
  const [loadingSlug, setLoadingSlug] = useState("");
  const [savingTypeSlug, setSavingTypeSlug] = useState("");
  const [savingPinnedSlug, setSavingPinnedSlug] = useState("");
  const [fixingFloatboxSlug, setFixingFloatboxSlug] = useState("");
  const [savingEdgeListKey, setSavingEdgeListKey] = useState("");
  const [savingEdgeSlug, setSavingEdgeSlug] = useState("");
  const [savingRetranslateSlug, setSavingRetranslateSlug] = useState("");
  const [error, setError] = useState("");
  const [isMobileTrope, setIsMobileTrope] = useState(false);
  const [mobileStage, setMobileStage] = useState<TropeMobileStage>("list");
  const [contentLanguage, setContentLanguage] = useState<TropeContentLanguage>("en");
  const [contentColumns, setContentColumns] = useState<TropeContentColumn[]>([]);
  const [searchMode, setSearchMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchItems, setSearchItems] = useState<TropeListItem[]>([]);
  const [searchMeta, setSearchMeta] = useState<TropeListMeta>(() => emptyTropeListMeta());
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [savingMarkdownSlug, setSavingMarkdownSlug] = useState("");
  const initializedRouteRef = useRef(false);
  const desktopAutoOpenRef = useRef(false);
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const contentPaneRef = useRef<HTMLElement | null>(null);
  const contentBodyRef = useRef<HTMLDivElement | null>(null);
  const contentColumnHScrollRef = useRef<HTMLDivElement | null>(null);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const markdownColumnRefs = useRef<Map<number, HTMLTextAreaElement>>(new Map());
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const activeListItemRef = useRef<HTMLButtonElement | null>(null);
  const pendingListCenterSlugRef = useRef("");
  const pendingContentScrollRatioRef = useRef<number | null>(null);
  const pendingPrependScrollRef = useRef<{ type: TropeType; scrollHeight: number; scrollTop: number } | null>(null);
  const mobileListScrollTopRef = useRef(0);
  const suppressAutoListLoadRef = useRef(false);
  const listsRef = useRef<Record<number, TropeListItem[]>>({});
  const listMetaRef = useRef<Record<number, TropeListMeta>>({});
  const loadingListTypeRef = useRef<TropeType | null>(null);
  const selectedRef = useRef<TropeItem | null>(null);

  const isSearchResultMode = Boolean(searchQuery);
  const activeList = isSearchResultMode ? searchItems : lists[activeType] || [];
  const activeListMeta = isSearchResultMode ? searchMeta : listMeta[activeType] || emptyTropeListMeta(activeList.length);
  const isActiveListLoading = isSearchResultMode ? loadingSearch : loadingListType === activeType;
  const visibleContentColumns: TropeContentColumn[] =
    contentColumns.length > 0
      ? contentColumns
        : [
          {
            mode: "content",
            slug: selected?.slug || loadingSlug || "",
            language: contentLanguage,
            item: selected,
            loading: Boolean(loadingSlug),
            error: ""
          }
        ];
  const columnLayoutClass =
    visibleContentColumns.length >= 3 ? "manyColumns" : visibleContentColumns.length === 2 ? "twoColumns" : "oneColumn";
  const oppositeContentLanguage = oppositeLanguage(contentLanguage);

  const writeTropeRoute = useCallback((type: TropeType, slug = "", mode: TropeHistoryMode = "push", panes: TropeRoutePane[] = []) => {
    if (mode === "none" || typeof window === "undefined") {
      return;
    }
    const cleanSlug = cleanTropeSlug(slug);
    const nextUrl = tropeRouteUrl(type, cleanSlug, panes);
    const nextState = tropeRouteState(type, cleanSlug, panes);
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl === nextUrl) {
      window.history.replaceState(nextState, "", nextUrl);
      return;
    }
    if (mode === "replace") {
      window.history.replaceState(nextState, "", nextUrl);
    } else {
      window.history.pushState(nextState, "", nextUrl);
    }
  }, []);

  const routePanesForColumns = useCallback(
    (columns: TropeContentColumn[] = contentColumns, primary: TropeItem | null = selectedRef.current || selected) => {
      if (!primary?.slug) {
        return [];
      }
      const panes: TropeRoutePane[] = [{ slug: primary.slug, language: contentLanguage }];
      for (const column of columns.slice(1)) {
        if (column.mode === "markdown") {
          continue;
        }
        const columnSlug = cleanTropeSlug(column.item?.slug || column.slug || "");
        if (!columnSlug) {
          continue;
        }
        panes.push({
          slug: columnSlug,
          language: column.language || contentLanguage
        });
      }
      return panes;
    },
    [contentColumns, contentLanguage, selected]
  );

  const routePanesFromRoute = useCallback((route: TropeRoute, fallbackLanguage: TropeContentLanguage) => {
    return route.paneSlugs.map((slug, index) => ({
      slug,
      language: route.explicitLanguages ? route.paneLanguages[index] || route.paneLanguages[0] || fallbackLanguage : fallbackLanguage
    }));
  }, []);

  const clearSearchState = useCallback(() => {
    setSearchMode(false);
    setSearchText("");
    setSearchQuery("");
    setSearchItems([]);
    setSearchMeta(emptyTropeListMeta());
  }, []);

  useEffect(() => {
    listsRef.current = lists;
  }, [lists]);

  useEffect(() => {
    setContentLanguage(storedTropeContentLanguage());
  }, []);

  useEffect(() => {
    listMetaRef.current = listMeta;
  }, [listMeta]);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    setContentColumns((current) => {
      if (!selected) {
        return [];
      }
      const firstColumn: TropeContentColumn = {
        mode: "content",
        slug: selected.slug,
        language: contentLanguage,
        item: selected,
        loading: false,
        error: ""
      };
      if (current[0]?.slug === selected.slug) {
        return [firstColumn, ...current.slice(1)];
      }
      return [firstColumn];
    });
  }, [contentLanguage, selected]);

  useEffect(() => {
    loadingListTypeRef.current = loadingListType;
  }, [loadingListType]);

  useEffect(() => {
    if (!searchMode) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [searchMode]);

  const loadList = useCallback(async (type: TropeType, mode: TropeListMode = "reset", options: { offset?: number; anchorSlug?: string } = {}) => {
    if (loadingListTypeRef.current === type) {
      return [];
    }
    const currentMeta = listMetaRef.current[type];
    if (mode === "append" && currentMeta && !currentMeta.hasMore) {
      return [];
    }
    if (mode === "prepend" && currentMeta && !currentMeta.hasBefore) {
      return [];
    }
    let offset = 0;
    let limit = LIST_PAGE_SIZE;
    if (mode === "append") {
      offset = Math.max(0, Math.trunc(options.offset ?? currentMeta?.nextOffset ?? listsRef.current[type]?.length ?? 0));
    } else if (mode === "prepend") {
      const firstOffset = Math.max(0, Math.trunc(currentMeta?.offset ?? 0));
      offset = Math.max(0, firstOffset - LIST_PAGE_SIZE);
      limit = Math.max(1, firstOffset - offset);
      if (type === activeType && isMobileTrope && mobileStage === "list") {
        const metrics = getWindowScrollMetrics();
        pendingPrependScrollRef.current = {
          type,
          scrollHeight: metrics.scrollHeight,
          scrollTop: metrics.scrollTop
        };
      } else {
        const listElement = type === activeType ? listScrollRef.current : null;
        if (listElement) {
          pendingPrependScrollRef.current = {
            type,
            scrollHeight: listElement.scrollHeight,
            scrollTop: listElement.scrollTop
          };
        }
      }
    } else if (mode === "anchor") {
      offset = 0;
      limit = ANCHOR_BEFORE_COUNT + ANCHOR_AFTER_COUNT + 1;
    }
    loadingListTypeRef.current = type;
    setLoadingListType(type);
    setError("");
    try {
      const params = new URLSearchParams({
        type: String(type),
        offset: String(offset),
        limit: String(limit)
      });
      if (mode === "anchor" && options.anchorSlug) {
        params.set("anchorSlug", options.anchorSlug);
        params.set("before", String(ANCHOR_BEFORE_COUNT));
        params.set("after", String(ANCHOR_AFTER_COUNT));
      }
      const response = await fetch(`/api/plot-tropes/browse/list?${params.toString()}`, { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      const nextItems = Array.isArray(payload.items) ? (payload.items as TropeListItem[]) : [];
      setLists((current) => {
        if (mode === "reset" || mode === "anchor") {
          return { ...current, [type]: nextItems };
        }
        const existing = current[type] || [];
        if (mode === "prepend") {
          const seen = new Set(nextItems.map((item) => item.slug));
          const merged = [...nextItems];
          for (const item of existing) {
            if (!seen.has(item.slug)) {
              seen.add(item.slug);
              merged.push(item);
            }
          }
          return { ...current, [type]: merged };
        }
        const seen = new Set(existing.map((item) => item.slug));
        const merged = [...existing];
        for (const item of nextItems) {
          if (!seen.has(item.slug)) {
            seen.add(item.slug);
            merged.push(item);
          }
        }
        return { ...current, [type]: merged };
      });
      setListMeta((current) => {
        const previous = current[type];
        const payloadMeta = {
          total: Number(payload.total || 0),
          offset: Number(payload.offset || 0),
          prevOffset: Number(payload.prevOffset || 0),
          nextOffset: Number(payload.nextOffset || offset + nextItems.length),
          hasBefore: Boolean(payload.hasBefore),
          hasMore: Boolean(payload.hasMore)
        };
        if (mode === "append" && previous) {
          return {
            ...current,
            [type]: {
              ...payloadMeta,
              offset: Number(previous.offset || 0),
              prevOffset: Number(previous.prevOffset || 0),
              hasBefore: Boolean(previous.hasBefore),
              nextOffset: payloadMeta.nextOffset,
              hasMore: payloadMeta.hasMore
            }
          };
        }
        if (mode === "prepend" && previous) {
          return {
            ...current,
            [type]: {
              ...payloadMeta,
              nextOffset: Number(previous.nextOffset || payloadMeta.nextOffset),
              hasMore: Boolean(previous.hasMore)
            }
          };
        }
        return { ...current, [type]: payloadMeta };
      });
      setLoadedTypes((current) => ({ ...current, [type]: true }));
      return nextItems;
    } catch (loadError) {
      if (mode === "prepend") {
        pendingPrependScrollRef.current = null;
      }
      setError(loadError instanceof Error ? loadError.message : "trope list load failed");
      return [];
    } finally {
      loadingListTypeRef.current = null;
      setLoadingListType((current) => (current === type ? null : current));
    }
  }, [activeType, isMobileTrope, mobileStage]);

  const applySourceContentRewrite = useCallback(
    (sourceSlugValue: string, sourceContent: string, language: TropeContentLanguage = contentLanguage) => {
      const sourceSlug = cleanTropeSlug(sourceSlugValue);
      if (!sourceSlug || !sourceContent) {
        return;
      }
      setSelected((current) =>
        current?.slug === sourceSlug
          ? {
              ...current,
              content: language === "ko" ? current.content : sourceContent,
              contentKo: language === "ko" ? sourceContent : current.contentKo
            }
          : current
      );
      setContentColumns((current) =>
        current.map((column) =>
          column.item?.slug === sourceSlug
            ? {
                ...column,
                item: {
                ...column.item,
                  content: language === "ko" ? column.item.content : sourceContent,
                  contentKo: language === "ko" ? sourceContent : column.item.contentKo
                }
              }
            : column
        )
      );
    },
    [contentLanguage]
  );

  const bindTropeRelations = useCallback((item: TropeItem | null | undefined) => {
    if (!item?.slug) {
      return;
    }
    const relationPatch = {
      edgeDebug: item.edgeDebug || {},
      ancestorTags: Array.isArray(item.ancestorTags) ? item.ancestorTags : []
    };
    setSelected((current) => (current?.slug === item.slug ? { ...current, ...relationPatch } : current));
    setContentColumns((current) =>
      current.map((column) =>
        column.item?.slug === item.slug
          ? {
              ...column,
              item: {
                ...column.item,
                ...relationPatch
              }
            }
          : column
      )
    );
  }, []);

  const openSlug = useCallback(
    async (slug: string, historyMode: TropeHistoryMode = "push", options: { sourceSlug?: string; language?: TropeContentLanguage } = {}) => {
      const cleanSlug = cleanTropeSlug(slug);
      if (!cleanSlug) {
        return null;
      }
      const loadLanguage = options.language || contentLanguage;
      if (isMobileTrope && mobileStage === "list") {
        mobileListScrollTopRef.current = getWindowScrollMetrics().scrollTop;
      }
      suppressAutoListLoadRef.current = true;
      setLoadingSlug(cleanSlug);
      if (historyMode === "none") {
        pendingListCenterSlugRef.current = cleanSlug;
      } else {
        pendingListCenterSlugRef.current = "";
      }
      setError("");
      try {
        const sourceSlug = cleanTropeSlug(options.sourceSlug || selectedRef.current?.slug || "");
        const relationsPromise = fetchTropeItemPayload(cleanSlug, {
          sourceSlug,
          language: loadLanguage,
          mode: "relations"
        }).catch((relationsError) => ({ relationsError }));
        const payload = await fetchTropeItemPayload(cleanSlug, {
          sourceSlug,
          language: loadLanguage,
          mode: "content"
        });
        const item = payload.item as TropeItem;
        const sourceContent = typeof payload?.result?.sourceContent === "string" ? payload.result.sourceContent : "";
        const sourceSlugFromResult = cleanTropeSlug(String(payload?.result?.sourceSlug || sourceSlug || ""));
        const itemType = normalizeTropeType(item.type);
        const listForType = listsRef.current[itemType] || [];
        const listHasItem = listForType.some((listItem) => listItem.slug === item.slug);
        const shouldCenterList = historyMode === "none" || !listHasItem;
        if (shouldCenterList) {
          pendingListCenterSlugRef.current = item.slug || cleanSlug;
        }
        if (sourceContent && sourceSlugFromResult) {
          applySourceContentRewrite(sourceSlugFromResult, sourceContent, loadLanguage);
        }
        setSelected(item);
        setActiveType(itemType);
        setMobileStage("detail");
        writeTropeRoute(itemType, item.slug || cleanSlug, historyMode, [{ slug: item.slug || cleanSlug, language: loadLanguage }]);
        void relationsPromise.then((relationsPayload) => {
          if ("relationsError" in relationsPayload) {
            return;
          }
          const relationsSourceContent =
            typeof relationsPayload?.result?.sourceContent === "string" ? relationsPayload.result.sourceContent : "";
          const relationsSourceSlug = cleanTropeSlug(String(relationsPayload?.result?.sourceSlug || sourceSlug || ""));
          if (relationsSourceContent && relationsSourceSlug) {
            applySourceContentRewrite(relationsSourceSlug, relationsSourceContent, loadLanguage);
          }
          bindTropeRelations(relationsPayload.item);
        });
        if (!listHasItem) {
          void loadList(itemType, "anchor", { anchorSlug: item.slug || cleanSlug });
        } else if (!loadedTypes[itemType]) {
          void loadList(itemType);
        }
        return item;
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "trope load failed");
        return null;
      } finally {
        suppressAutoListLoadRef.current = false;
        setLoadingSlug("");
      }
    },
    [applySourceContentRewrite, bindTropeRelations, contentLanguage, isMobileTrope, loadList, loadedTypes, mobileStage, writeTropeRoute]
  );

  const openColumnSlug = useCallback(async (slug: string, sourceColumnIndex: number, event?: MouseEvent<HTMLButtonElement>, languageOverride?: TropeContentLanguage) => {
    const cleanSlug = cleanTropeSlug(slug);
    const sourceColumn = contentColumns[sourceColumnIndex];
    const sourceSlug = cleanTropeSlug(sourceColumn?.item?.slug || sourceColumn?.slug || selectedRef.current?.slug || "");
    const sourceLanguage = sourceColumnIndex === 0 ? contentLanguage : sourceColumn?.language || contentLanguage;
    const columnLanguage = languageOverride || sourceLanguage;
    if (!cleanSlug || !sourceSlug) {
      return;
    }
    if (isMobileTrope || event?.metaKey || event?.ctrlKey) {
      void openSlug(cleanSlug, "push", { sourceSlug, language: columnLanguage });
      return;
    }

    const targetIndex = Math.max(0, sourceColumnIndex + 1);
    const nextColumns: TropeContentColumn[] = [
      ...contentColumns.slice(0, targetIndex),
      {
        mode: "content",
        slug: cleanSlug,
        language: columnLanguage,
        item: null,
        loading: true,
        error: ""
      }
    ];
    setContentColumns(nextColumns);
    const routePanes = routePanesForColumns(nextColumns);
    if (routePanes.length > 0) {
      writeTropeRoute(normalizeTropeType(selectedRef.current?.type || selected?.type || activeType), routePanes[0].slug, "push", routePanes);
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const element = contentColumnHScrollRef.current;
        if (element) {
          element.scrollTo({ left: element.scrollWidth, behavior: "smooth" });
        }
      });
    });

    try {
      const relationsPromise = fetchTropeItemPayload(cleanSlug, {
        sourceSlug,
        language: columnLanguage,
        mode: "relations"
      }).catch((relationsError) => ({ relationsError }));
      const payload = await fetchTropeItemPayload(cleanSlug, {
        sourceSlug,
        language: columnLanguage,
        mode: "content"
      });
      const item = payload.item as TropeItem;
      const sourceContent = typeof payload?.result?.sourceContent === "string" ? payload.result.sourceContent : "";
      const sourceSlugFromResult = cleanTropeSlug(String(payload?.result?.sourceSlug || sourceSlug || ""));
      if (sourceContent && sourceSlugFromResult) {
        applySourceContentRewrite(sourceSlugFromResult, sourceContent, columnLanguage);
      }
      setContentColumns((current) =>
        current.map((column, index) => {
          if (index === targetIndex && column.slug === cleanSlug && column.language === columnLanguage) {
            return {
              mode: "content",
              slug: item.slug || cleanSlug,
              language: columnLanguage,
              item,
              loading: false,
              error: ""
            };
          }
          return column;
        })
      );
      const loadedColumns: TropeContentColumn[] = nextColumns.map((column, index) =>
        index === targetIndex && column.slug === cleanSlug && column.language === columnLanguage
          ? {
              mode: "content",
              slug: item.slug || cleanSlug,
              language: columnLanguage,
              item,
              loading: false,
              error: ""
            }
          : column
      );
      const loadedRoutePanes = routePanesForColumns(loadedColumns);
      if (loadedRoutePanes.length > 0) {
        writeTropeRoute(normalizeTropeType(selectedRef.current?.type || selected?.type || activeType), loadedRoutePanes[0].slug, "replace", loadedRoutePanes);
      }
      void relationsPromise.then((relationsPayload) => {
        if ("relationsError" in relationsPayload) {
          return;
        }
        const relationsSourceContent =
          typeof relationsPayload?.result?.sourceContent === "string" ? relationsPayload.result.sourceContent : "";
        const relationsSourceSlug = cleanTropeSlug(String(relationsPayload?.result?.sourceSlug || sourceSlug || ""));
        if (relationsSourceContent && relationsSourceSlug) {
          applySourceContentRewrite(relationsSourceSlug, relationsSourceContent, columnLanguage);
        }
        bindTropeRelations(relationsPayload.item);
      });
    } catch (loadError) {
      setContentColumns((current) =>
        current.map((column, index) =>
          index === targetIndex && column.slug === cleanSlug && column.language === columnLanguage
            ? {
                ...column,
                item: null,
                loading: false,
                error: loadError instanceof Error ? loadError.message : "column load failed"
              }
            : column
        )
      );
    }
  }, [activeType, applySourceContentRewrite, bindTropeRelations, contentColumns, contentLanguage, isMobileTrope, openSlug, routePanesForColumns, selected?.type, writeTropeRoute]);

  const openMarkdownColumn = useCallback(
    (sourceColumnIndex: number) => {
      if (isMobileTrope) {
        return;
      }
      const fallbackColumns: TropeContentColumn[] = selected
        ? [
            {
              mode: "content",
              slug: selected.slug,
              language: contentLanguage,
              item: selected,
              loading: false,
              error: ""
            }
          ]
        : [];
      const currentColumns = contentColumns.length > 0 ? contentColumns : fallbackColumns;
      const sourceColumn = currentColumns[sourceColumnIndex];
      if (!sourceColumn || sourceColumn.mode === "markdown") {
        return;
      }
      const sourceItem = sourceColumnIndex === 0 ? selected || sourceColumn.item : sourceColumn.item;
      if (!sourceItem) {
        return;
      }
      const sourceLanguage = sourceColumnIndex === 0 ? contentLanguage : sourceColumn.language || contentLanguage;
      const sourceSlug = cleanTropeSlug(sourceItem.slug);
      if (!sourceSlug) {
        return;
      }
      const targetIndex = sourceColumnIndex + 1;
      const existingTarget = currentColumns[targetIndex];
      const nextColumns =
        existingTarget?.mode === "markdown" &&
        existingTarget.sourceSlug === sourceSlug &&
        existingTarget.language === sourceLanguage
          ? currentColumns.slice(0, targetIndex)
          : [
              ...currentColumns.slice(0, targetIndex),
              {
                mode: "markdown" as const,
                slug: sourceSlug,
                sourceSlug,
                language: sourceLanguage,
                item: sourceItem,
                loading: false,
                error: "",
                markdownDraft: contentFor(sourceItem, sourceLanguage)
              }
            ];
      setContentColumns(nextColumns);
      const routePanes = routePanesForColumns(nextColumns);
      if (routePanes.length > 0) {
        writeTropeRoute(normalizeTropeType(selectedRef.current?.type || selected?.type || activeType), routePanes[0].slug, "replace", routePanes);
      }
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const element = contentColumnHScrollRef.current;
          if (element) {
            element.scrollTo({ left: element.scrollWidth, behavior: "smooth" });
          }
        });
      });
    },
    [activeType, contentColumns, contentLanguage, isMobileTrope, routePanesForColumns, selected, writeTropeRoute]
  );

  const handleColumnInternalLink = useCallback(
    (columnIndex: number) => (slug: string, event?: MouseEvent<HTMLButtonElement>) => {
      void openColumnSlug(slug, columnIndex, event);
    },
    [openColumnSlug]
  );

  const showListRoute = useCallback(
    (type: TropeType, historyMode: TropeHistoryMode = "push") => {
      setActiveType(type);
      setSelected(null);
      setContentColumns([]);
      pendingListCenterSlugRef.current = "";
      setError("");
      setMobileStage("list");
      writeTropeRoute(type, "", historyMode);
      if (!loadedTypes[type] && loadingListType !== type) {
        void loadList(type);
      }
    },
    [loadList, loadedTypes, loadingListType, writeTropeRoute]
  );

  const restoreTropeRoute = useCallback(
    async (route: TropeRoute) => {
      if (!route.slug) {
        showListRoute(route.type, "none");
        return;
      }

      const fallbackLanguage = route.explicitLanguages ? route.paneLanguages[0] || "en" : storedTropeContentLanguage();
      const primaryLanguage = route.explicitLanguages ? route.paneLanguages[0] || fallbackLanguage : fallbackLanguage;
      persistTropeContentLanguage(primaryLanguage);
      setContentLanguage(primaryLanguage);

      const routePanes = routePanesFromRoute(route, primaryLanguage);
      const primaryItem = await openSlug(route.slug, "none", { language: primaryLanguage });
      if (!primaryItem) {
        return;
      }

      const primaryPane: TropeRoutePane = {
        slug: primaryItem.slug || route.slug,
        language: routePanes[0]?.language || primaryLanguage
      };
      const restoredPanes = [primaryPane, ...routePanes.slice(1)];
      const primaryType = normalizeTropeType(primaryItem.type);
      if (restoredPanes.length <= 1 || isMobileTrope) {
        writeTropeRoute(primaryType, primaryPane.slug, "replace", [primaryPane]);
        return;
      }

      setContentColumns(
        restoredPanes.map((pane, index) => ({
          mode: "content",
          slug: pane.slug,
          language: pane.language || primaryLanguage,
          item: index === 0 ? primaryItem : null,
          loading: index > 0,
          error: ""
        }))
      );
      writeTropeRoute(primaryType, primaryPane.slug, "replace", restoredPanes);

      window.requestAnimationFrame(() => {
        const element = contentColumnHScrollRef.current;
        if (element) {
          element.scrollTo({ left: element.scrollWidth, behavior: "auto" });
        }
      });

      restoredPanes.slice(1).forEach((pane, offset) => {
        const targetIndex = offset + 1;
        const paneSlug = cleanTropeSlug(pane.slug);
        const paneLanguage = pane.language || primaryLanguage;
        const sourceSlug = cleanTropeSlug(restoredPanes[targetIndex - 1]?.slug || primaryPane.slug);
        if (!paneSlug || !sourceSlug) {
          return;
        }

        const relationsPromise = fetchTropeItemPayload(paneSlug, {
          sourceSlug,
          language: paneLanguage,
          mode: "relations"
        }).catch((relationsError) => ({ relationsError }));

        void fetchTropeItemPayload(paneSlug, {
          sourceSlug,
          language: paneLanguage,
          mode: "content"
        })
          .then((payload) => {
            const item = payload.item as TropeItem;
            const sourceContent = typeof payload?.result?.sourceContent === "string" ? payload.result.sourceContent : "";
            const sourceSlugFromResult = cleanTropeSlug(String(payload?.result?.sourceSlug || sourceSlug || ""));
            if (sourceContent && sourceSlugFromResult) {
              applySourceContentRewrite(sourceSlugFromResult, sourceContent, paneLanguage);
            }
            setContentColumns((current) =>
              current.map((column, index) =>
                index === targetIndex && column.slug === paneSlug && column.language === paneLanguage
                  ? {
                      mode: "content",
                      slug: item.slug || paneSlug,
                      language: paneLanguage,
                      item,
                      loading: false,
                      error: ""
                    }
                  : column
              )
            );
          })
          .catch((loadError) => {
            setContentColumns((current) =>
              current.map((column, index) =>
                index === targetIndex && column.slug === paneSlug && column.language === paneLanguage
                  ? {
                      ...column,
                      item: null,
                      loading: false,
                      error: loadError instanceof Error ? loadError.message : "column load failed"
                    }
                  : column
              )
            );
          });

        void relationsPromise.then((relationsPayload) => {
          if ("relationsError" in relationsPayload) {
            return;
          }
          const relationsSourceContent =
            typeof relationsPayload?.result?.sourceContent === "string" ? relationsPayload.result.sourceContent : "";
          const relationsSourceSlug = cleanTropeSlug(String(relationsPayload?.result?.sourceSlug || sourceSlug || ""));
          if (relationsSourceContent && relationsSourceSlug) {
            applySourceContentRewrite(relationsSourceSlug, relationsSourceContent, paneLanguage);
          }
          bindTropeRelations(relationsPayload.item);
        });
      });
    },
    [
      applySourceContentRewrite,
      bindTropeRelations,
      isMobileTrope,
      openSlug,
      routePanesFromRoute,
      showListRoute,
      writeTropeRoute
    ]
  );

  const loadSearchResults = useCallback(
    async (query: string, mode: "reset" | "append" = "reset") => {
      const cleanQuery = query.trim();
      if (!cleanQuery || loadingSearch) {
        return [];
      }
      const offset = mode === "append" ? Math.max(0, searchMeta.nextOffset || searchItems.length) : 0;
      setLoadingSearch(true);
      setError("");
      try {
        const params = new URLSearchParams({
          type: String(activeType),
          offset: String(offset),
          limit: String(LIST_PAGE_SIZE),
          search: cleanQuery
        });
        const response = await fetch(`/api/plot-tropes/browse/list?${params.toString()}`, { cache: "no-store" });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `HTTP ${response.status}`);
        }
        const nextItems = Array.isArray(payload.items) ? (payload.items as TropeListItem[]) : [];
        const total = Number(payload.total || 0);
        if (mode === "reset" && total === 1 && nextItems[0]?.slug) {
          clearSearchState();
          void openSlug(nextItems[0].slug);
          return nextItems;
        }
        setSearchMode(true);
        setSearchQuery(cleanQuery);
        setSearchItems((current) => {
          if (mode === "reset") {
            return nextItems;
          }
          const seen = new Set(current.map((item) => item.slug));
          const merged = [...current];
          for (const item of nextItems) {
            if (!seen.has(item.slug)) {
              seen.add(item.slug);
              merged.push(item);
            }
          }
          return merged;
        });
        setSearchMeta({
          total,
          offset: Number(payload.offset || 0),
          prevOffset: Number(payload.prevOffset || 0),
          nextOffset: Number(payload.nextOffset || offset + nextItems.length),
          hasBefore: false,
          hasMore: Boolean(payload.hasMore)
        });
        if (mode === "reset") {
          const listElement = listScrollRef.current;
          if (listElement) {
            listElement.scrollTo({ top: 0, behavior: "auto" });
          } else if (isMobileTrope && mobileStage === "list") {
            window.scrollTo({ top: 0, behavior: "auto" });
          }
        }
        return nextItems;
      } catch (searchError) {
        setError(searchError instanceof Error ? searchError.message : "trope search failed");
        return [];
      } finally {
        setLoadingSearch(false);
      }
    },
    [
      activeType,
      clearSearchState,
      isMobileTrope,
      loadingSearch,
      mobileStage,
      openSlug,
      searchItems.length,
      searchMeta.nextOffset
    ]
  );

  const submitSearch = useCallback(() => {
    const cleanQuery = searchText.trim();
    if (!cleanQuery) {
      clearSearchState();
      return;
    }
    void (async () => {
      if (looksLikeTropeSlugSearch(cleanQuery)) {
        const item = await openSlug(cleanTropeSlug(cleanQuery));
        if (item) {
          clearSearchState();
          return;
        }
      }
      await loadSearchResults(cleanQuery, "reset");
    })();
  }, [clearSearchState, loadSearchResults, openSlug, searchText]);

  const loadMoreActiveList = useCallback(() => {
    if (isSearchResultMode) {
      if (loadingSearch || !activeListMeta.hasMore) {
        return;
      }
      void loadSearchResults(searchQuery, "append");
      return;
    }
    if (loadingListType === activeType || !activeListMeta.hasMore) {
      return;
    }
    void loadList(activeType, "append", { offset: activeListMeta.nextOffset || activeList.length });
  }, [
    activeList.length,
    activeListMeta.hasMore,
    activeListMeta.nextOffset,
    activeType,
    isSearchResultMode,
    loadList,
    loadSearchResults,
    loadingListType,
    loadingSearch,
    searchQuery
  ]);

  const loadPreviousActiveList = useCallback(() => {
    if (isSearchResultMode) {
      return;
    }
    if (loadingListType === activeType || !activeListMeta.hasBefore) {
      return;
    }
    void loadList(activeType, "prepend");
  }, [activeListMeta.hasBefore, activeType, isSearchResultMode, loadList, loadingListType]);

  const moveSelectedInList = useCallback(
    async (direction: -1 | 1) => {
      if (!selected || loadingSlug || loadingListTypeRef.current === activeType) {
        return;
      }
      const selectedType = normalizeTropeType(selected.type);
      if (selectedType !== activeType) {
        return;
      }
      const currentList = isSearchResultMode ? searchItems : listsRef.current[activeType] || [];
      const currentIndex = currentList.findIndex((item) => item.slug === selected.slug);
      if (currentIndex < 0) {
        if (!isSearchResultMode) {
          pendingListCenterSlugRef.current = selected.slug;
          void loadList(activeType, "anchor", { anchorSlug: selected.slug });
        }
        return;
      }

      const nextIndex = currentIndex + direction;
      if (nextIndex >= 0 && nextIndex < currentList.length) {
        void openSlug(currentList[nextIndex].slug);
        return;
      }

      const meta = isSearchResultMode ? searchMeta : listMetaRef.current[activeType];
      if (direction > 0) {
        if (!meta?.hasMore) {
          return;
        }
        const appendedItems = isSearchResultMode
          ? await loadSearchResults(searchQuery, "append")
          : await loadList(activeType, "append", { offset: meta.nextOffset || currentList.length });
        if (appendedItems[0]?.slug) {
          void openSlug(appendedItems[0].slug);
        }
        return;
      }

      if (!meta?.hasBefore || isSearchResultMode) {
        return;
      }
      const prependedItems = await loadList(activeType, "prepend");
      const previousItem = prependedItems[prependedItems.length - 1];
      if (previousItem?.slug) {
        void openSlug(previousItem.slug);
      }
    },
    [activeType, isSearchResultMode, loadList, loadSearchResults, loadingSlug, openSlug, searchItems, searchMeta, searchQuery, selected]
  );

  const applyTypeValue = useCallback((slug: string, type: TropeType) => {
    setSelected((current) => (current?.slug === slug ? { ...current, type } : current));
    setContentColumns((current) =>
      current.map((column) => (column.item?.slug === slug ? { ...column, item: { ...column.item, type } } : column))
    );
    setLists((current) => {
      const nextEntries = Object.entries(current).map(([typeKey, items]) => {
        const listType = Number(typeKey);
        const nextItems =
          listType === type
            ? items.map((item) => (item.slug === slug ? { ...item, type } : item))
            : items.filter((item) => item.slug !== slug);
        return [typeKey, nextItems] as const;
      });
      return Object.fromEntries(nextEntries);
    });
  }, []);

  const applyTypeValues = useCallback((slugs: string[], type: TropeType) => {
    const slugSet = new Set(slugs.map(cleanTropeSlug).filter(Boolean));
    if (slugSet.size === 0) {
      return;
    }
    setSelected((current) => (current && slugSet.has(current.slug) ? { ...current, type } : current));
    setContentColumns((current) =>
      current.map((column) =>
        column.item && slugSet.has(column.item.slug) ? { ...column, item: { ...column.item, type } } : column
      )
    );
    setLists((current) => {
      const nextEntries = Object.entries(current).map(([typeKey, items]) => {
        const listType = Number(typeKey);
        const nextItems =
          listType === type
            ? items.map((item) => (slugSet.has(item.slug) ? { ...item, type } : item))
            : items.filter((item) => !slugSet.has(item.slug));
        return [typeKey, nextItems] as const;
      });
      return Object.fromEntries(nextEntries);
    });
  }, []);

  const applyPinnedValue = useCallback((slug: string, pinned: number) => {
    const cleanSlug = cleanTropeSlug(slug);
    const nextPinned = pinned ? 1 : 0;
    setSelected((current) => (current?.slug === cleanSlug ? { ...current, pinned: nextPinned } : current));
    setContentColumns((current) =>
      current.map((column) =>
        column.item?.slug === cleanSlug ? { ...column, item: { ...column.item, pinned: nextPinned } } : column
      )
    );
    setLists((current) => {
      const nextEntries = Object.entries(current).map(([typeKey, items]) => {
        const nextItems = sortTropeListItems(
          items.map((item) => (item.slug === cleanSlug ? { ...item, pinned: nextPinned } : item))
        );
        return [typeKey, nextItems] as const;
      });
      return Object.fromEntries(nextEntries);
    });
  }, []);

  const applyRetranslateQueued = useCallback((item: TropeItem) => {
    const cleanSlug = cleanTropeSlug(item.slug);
    if (!cleanSlug) {
      return;
    }
    setSelected((current) => (current?.slug === cleanSlug ? { ...current, ...item, stateKo: item.stateKo } : current));
    setContentColumns((current) =>
      current.map((column) =>
        column.item?.slug === cleanSlug
          ? {
              ...column,
              slug: cleanSlug,
              item: {
                ...column.item,
                ...item,
                stateKo: item.stateKo
              }
            }
          : column
      )
    );
    setLists((current) => {
      const itemType = normalizeTropeType(item.type);
      const existing = current[itemType] || [];
      return {
        ...current,
        [itemType]: existing.map((listItem) =>
          listItem.slug === cleanSlug
            ? {
                ...listItem,
                title: item.title,
                titleKo: item.titleKo,
                type: item.type,
                state: item.state,
                stateKo: item.stateKo,
                hasIssue: item.hasIssue,
                pinned: item.pinned,
                contentChars: item.contentChars,
                updatedTs: item.updatedTs
              }
            : listItem
        )
      };
    });
    setSearchItems((current) =>
      current.map((listItem) =>
        listItem.slug === cleanSlug
          ? {
              ...listItem,
              title: item.title,
              titleKo: item.titleKo,
              type: item.type,
              state: item.state,
              stateKo: item.stateKo,
              hasIssue: item.hasIssue,
              pinned: item.pinned,
              contentChars: item.contentChars,
              updatedTs: item.updatedTs
            }
          : listItem
      )
    );
  }, []);

  const toggleSelectedPinned = useCallback(async () => {
    if (!selected || savingPinnedSlug === selected.slug) {
      return;
    }
    const slug = selected.slug;
    const previousPinned = selected.pinned ? 1 : 0;
    const nextPinned = previousPinned ? 0 : 1;
    setSavingPinnedSlug(slug);
    setError("");
    applyPinnedValue(slug, nextPinned);
    try {
      const response = await fetch("/api/plot-tropes/browse/pinned", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, pinned: nextPinned })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      const savedPinned = Number(payload?.item?.pinned || 0) ? 1 : 0;
      applyPinnedValue(String(payload?.item?.slug || slug), savedPinned);
    } catch (saveError) {
      applyPinnedValue(slug, previousPinned);
      setError(saveError instanceof Error ? saveError.message : "trope pinned update failed");
    } finally {
      setSavingPinnedSlug((current) => (current === slug ? "" : current));
    }
  }, [applyPinnedValue, savingPinnedSlug, selected]);

  const switchTropeType = useCallback(async (item: Pick<TropeItem, "slug" | "type">, nextType: TropeType, scope: "selected" | "column" = "selected") => {
    const slug = cleanTropeSlug(item.slug || "");
    if (!slug || savingTypeSlug === slug) {
      return;
    }
    const previousType = normalizeTropeType(item.type);
    if (!switchableTropeTypeOptions(previousType).includes(nextType)) {
      return;
    }
    // type을 바꾼 항목은 현재 리스트에서 빠지고 그 자리 뒤의 항목을 이어 선택한다.
    // 낙관 적용으로 리스트에서 먼저 사라지므로 위치 계산과 실패 복원용 스냅샷을 미리 확보한다.
    const sourceList = isSearchResultMode ? searchItems : listsRef.current[previousType] || [];
    const currentIndex = sourceList.findIndex((listItem) => listItem.slug === slug);
    const previousTypeList = (listsRef.current[previousType] || []).slice();
    setSavingTypeSlug(slug);
    setError("");
    applyTypeValue(slug, nextType);
    try {
      const response = await fetch("/api/plot-tropes/browse/type", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, type: nextType, cascadeChildIndexes: previousType === 2 && nextType === 3 })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      const savedItem = payload?.item as TropeItem | undefined;
      const savedSlug = cleanTropeSlug(savedItem?.slug || slug);
      const savedType = normalizeTropeType(savedItem?.type ?? nextType);
      const changedChildSlugs = Array.isArray(payload?.changedChildSlugs)
          ? payload.changedChildSlugs.map((value: unknown) => cleanTropeSlug(String(value || ""))).filter(Boolean)
          : [];
      if (savedItem?.slug) {
        setContentColumns((current) =>
          current.map((column) =>
            column.item?.slug === savedSlug ? { ...column, slug: savedSlug, item: { ...column.item, ...savedItem, type: savedType } } : column
          )
        );
      }
      applyTypeValue(savedSlug, savedType);
      applyTypeValues(changedChildSlugs, 3);
      if (scope === "selected") {
        // 헤더와 리스트는 원래 type에 그대로 두고, cascade로 함께 옮겨간 항목을 제외한 다음 항목을 이어 선택한다.
        const excludedSlugs = new Set<string>([savedSlug, ...changedChildSlugs]);
        if (isSearchResultMode) {
          setSearchItems((current) => current.filter((listItem) => !excludedSlugs.has(listItem.slug)));
        } else if (loadedTypes[savedType]) {
          // 새 type의 리스트가 이미 불려 있으면 이 항목이 빠진 채로 남으므로 다음 방문 때 다시 읽게 한다.
          setLoadedTypes((current) => ({ ...current, [savedType]: false }));
        }
        const nextItem =
          currentIndex >= 0
            ? sourceList.slice(currentIndex + 1).find((listItem) => !excludedSlugs.has(listItem.slug)) ||
              sourceList
                .slice(0, currentIndex)
                .reverse()
                .find((listItem) => !excludedSlugs.has(listItem.slug)) ||
              null
            : null;
        if (nextItem) {
          void openSlug(nextItem.slug, "replace");
        } else {
          if (isSearchResultMode) {
            clearSearchState();
          }
          showListRoute(previousType, "replace");
        }
      }
    } catch (saveError) {
      applyTypeValue(slug, previousType);
      // 낙관 적용으로 previousType 리스트에서 빠진 항목을 되살린다.
      setLists((current) =>
        current[previousType] && !current[previousType].some((listItem) => listItem.slug === slug)
          ? { ...current, [previousType]: previousTypeList }
          : current
      );
      setError(saveError instanceof Error ? saveError.message : "trope type update failed");
    } finally {
      setSavingTypeSlug((current) => (current === slug ? "" : current));
    }
  }, [applyTypeValue, applyTypeValues, clearSearchState, isSearchResultMode, loadedTypes, openSlug, savingTypeSlug, searchItems, showListRoute]);

  const enqueueRetranslation = useCallback(
    async (item: TropeItem | null | undefined) => {
      const slug = cleanTropeSlug(item?.slug || "");
      if (!item || !slug || savingRetranslateSlug === slug) {
        return;
      }
      const stateKo = Number(item.stateKo || 0);
      if (stateKo === 1 || stateKo === 5) {
        return;
      }
      const hasCompleteTranslation = Boolean(item.titleKo?.trim() && item.contentKo?.trim());
      if (hasCompleteTranslation && typeof window !== "undefined" && !window.confirm("다시 번역?")) {
        return;
      }

      setSavingRetranslateSlug(slug);
      setError("");
      try {
        const response = await fetch("/api/plot-tropes/browse/retranslate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slug })
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `HTTP ${response.status}`);
        }
        const savedItem = payload?.item as TropeItem | undefined;
        if (!savedItem?.slug) {
          throw new Error("queued trope row missing");
        }
        applyRetranslateQueued(savedItem);
      } catch (queueError) {
        setError(queueError instanceof Error ? queueError.message : "trope retranslation queue failed");
      } finally {
        setSavingRetranslateSlug((current) => (current === slug ? "" : current));
      }
    },
    [applyRetranslateQueued, savingRetranslateSlug]
  );

  const fixSelectedFloatbox = useCallback(async () => {
    if (!selected || fixingFloatboxSlug === selected.slug) {
      return;
    }
    const slug = selected.slug;
    setFixingFloatboxSlug(slug);
    setError("");
    try {
      const response = await fetch("/api/plot-tropes/browse/fix-floatbox", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, language: contentLanguage })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      const item = payload?.item as TropeItem | undefined;
      if (item) {
        setSelected(item);
        setLists((current) => {
          const itemType = normalizeTropeType(item.type);
          const existing = current[itemType] || [];
          return {
            ...current,
            [itemType]: existing.map((listItem) =>
              listItem.slug === item.slug
                ? {
                    ...listItem,
                    title: item.title,
                    type: item.type,
                    state: item.state,
                    hasIssue: item.hasIssue,
                    contentChars: item.contentChars,
                    updatedTs: item.updatedTs
                  }
                : listItem
            )
          };
        });
      }
    } catch (fixError) {
      setError(fixError instanceof Error ? fixError.message : "floatbox fix failed");
    } finally {
      setFixingFloatboxSlug((current) => (current === slug ? "" : current));
    }
  }, [contentLanguage, fixingFloatboxSlug, selected]);

  const assignEdgeList = useCallback(
    async ({ childSlugs, listKey, forceChildMedia = false }: { childSlugs: string[]; listKey: string; forceChildMedia?: boolean }) => {
      if (!selected || (selected.type !== 2 && selected.type !== 3) || childSlugs.length === 0 || savingEdgeListKey) {
        return;
      }
      const slug = selected.slug;
      const savingKey = forceChildMedia ? `${listKey}:media` : listKey;
      setSavingEdgeListKey(savingKey);
      setError("");
      try {
        const response = await fetch("/api/plot-tropes/browse/edge-list", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parentSlug: slug, childSlugs, forceChildMedia, language: contentLanguage })
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `HTTP ${response.status}`);
        }
        const nextEdgeDebug = payload?.result?.edgeDebug || {};
        const parentContent = typeof payload?.result?.parentContent === "string" ? payload.result.parentContent : "";
        const mediaPromotedChildSlugs = Array.isArray(payload?.result?.mediaPromotedChildSlugs)
          ? payload.result.mediaPromotedChildSlugs.map((value: unknown) => cleanTropeSlug(String(value || ""))).filter(Boolean)
          : [];
        applyTypeValues(mediaPromotedChildSlugs, 3);
        setSelected((current) =>
          current?.slug === slug
            ? {
                ...current,
                content: contentLanguage === "ko" ? current.content : parentContent || current.content,
                contentKo: contentLanguage === "ko" ? parentContent || current.contentKo : current.contentKo,
                edgeDebug: nextEdgeDebug
              }
            : current
        );
      } catch (saveError) {
        setError(saveError instanceof Error ? saveError.message : "edge list update failed");
      } finally {
        setSavingEdgeListKey((current) => (current === savingKey ? "" : current));
      }
    },
    [applyTypeValues, contentLanguage, savingEdgeListKey, selected]
  );

  const applyParentEdgePayload = useCallback(
    (parentSlug: string, parentLanguage: TropeContentLanguage, nextEdgeDebug: Record<string, { type: number }>, parentContent = "") => {
      const cleanParentSlug = cleanTropeSlug(parentSlug);
      setSelected((current) =>
        current?.slug === cleanParentSlug
          ? {
              ...current,
              content: parentLanguage === "ko" ? current.content : parentContent || current.content,
              contentKo: parentLanguage === "ko" ? parentContent || current.contentKo : current.contentKo,
              edgeDebug: nextEdgeDebug
            }
          : current
      );
      setContentColumns((current) =>
        current.map((column) =>
          column.item?.slug === cleanParentSlug
            ? {
                ...column,
                item: {
                  ...column.item,
                  content: parentLanguage === "ko" ? column.item.content : parentContent || column.item.content,
                  contentKo: parentLanguage === "ko" ? parentContent || column.item.contentKo : column.item.contentKo,
                  edgeDebug: nextEdgeDebug
                }
              }
            : column
        )
      );
    },
    []
  );

  const updateSingleEdgeForParent = useCallback(
    async (parent: Pick<TropeItem, "slug" | "type"> | null | undefined, parentLanguage: TropeContentLanguage, childSlug: string, action: "add" | "delete") => {
      if (!parent || (parent.type !== 2 && parent.type !== 3) || savingEdgeSlug) {
        return;
      }
      const parentSlug = parent.slug;
      const cleanChildSlug = cleanTropeSlug(childSlug);
      if (!cleanChildSlug.startsWith("Main/")) {
        return;
      }
      setSavingEdgeSlug(cleanChildSlug);
      setError("");
      try {
        const response = await fetch("/api/plot-tropes/browse/edge", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parentSlug, childSlug: cleanChildSlug, action, language: parentLanguage })
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `HTTP ${response.status}`);
        }
        const nextEdgeDebug = payload?.result?.edgeDebug || {};
        const parentContent = typeof payload?.result?.parentContent === "string" ? payload.result.parentContent : "";
        const mediaPromotedChildSlugs = Array.isArray(payload?.result?.mediaPromotedChildSlugs)
          ? payload.result.mediaPromotedChildSlugs.map((value: unknown) => cleanTropeSlug(String(value || ""))).filter(Boolean)
          : [];
        applyTypeValues(mediaPromotedChildSlugs, 3);
        applyParentEdgePayload(parentSlug, parentLanguage, nextEdgeDebug, parentContent);
      } catch (saveError) {
        setError(saveError instanceof Error ? saveError.message : "edge update failed");
      } finally {
        setSavingEdgeSlug((current) => (current === cleanChildSlug ? "" : current));
      }
    },
    [applyParentEdgePayload, applyTypeValues, savingEdgeSlug]
  );

  const setSingleEdgeType = useCallback(
    async (childSlug: string, type: TropeType, parent?: Pick<TropeItem, "slug" | "type"> | null) => {
      const parentItem = parent || selected;
      if (!parentItem || (parentItem.type !== 2 && parentItem.type !== 3) || savingEdgeSlug) {
        return;
      }
      const parentSlug = parentItem.slug;
      const cleanChildSlug = cleanTropeSlug(childSlug);
      if (!cleanChildSlug.startsWith("Main/")) {
        return;
      }
      setSavingEdgeSlug(cleanChildSlug);
      setError("");
      try {
        const response = await fetch("/api/plot-tropes/browse/type", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slug: cleanChildSlug, type })
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || `HTTP ${response.status}`);
        }
        const item = payload?.item as TropeItem | undefined;
        const savedSlug = cleanTropeSlug(item?.slug || cleanChildSlug);
        const savedType = normalizeTropeType(item?.type ?? type);
        applyTypeValue(savedSlug, savedType);
        const updateDebug = (item: TropeItem) => {
          const nextEdgeDebug = { ...(item.edgeDebug || {}) };
          const value = { type: savedType };
          nextEdgeDebug[savedSlug] = value;
          nextEdgeDebug[savedSlug.toLowerCase()] = value;
          return { ...item, edgeDebug: nextEdgeDebug };
        };
        setSelected((current) => (current?.slug === parentSlug ? updateDebug(current) : current));
        setContentColumns((current) =>
          current.map((column) =>
            column.item?.slug === parentSlug ? { ...column, item: updateDebug(column.item) } : column
          )
        );
      } catch (saveError) {
        setError(saveError instanceof Error ? saveError.message : "edge type update failed");
      } finally {
        setSavingEdgeSlug((current) => (current === cleanChildSlug ? "" : current));
      }
    },
    [applyTypeValue, savingEdgeSlug, selected]
  );

  const scrollMarkdownColumnDown = useCallback((columnIndex: number) => {
    const element = markdownColumnRefs.current.get(columnIndex);
    if (!element) {
      return;
    }
    element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
  }, []);

  const copyTextToClipboard = useCallback(async (content: string) => {
    if (!content) {
      return;
    }
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = content;
      textarea.setAttribute("readonly", "true");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  }, []);

  const updateMarkdownColumnDraft = useCallback((columnIndex: number, draft: string) => {
    setContentColumns((current) =>
      current.map((column, index) => (index === columnIndex && column.mode === "markdown" ? { ...column, markdownDraft: draft } : column))
    );
  }, []);

  const saveMarkdownColumn = useCallback(async (columnIndex: number) => {
    const column = contentColumns[columnIndex];
    if (!column || column.mode !== "markdown" || !column.item) {
      return;
    }
    const slug = cleanTropeSlug(column.sourceSlug || column.item.slug);
    const language = column.language || contentLanguage;
    const originalContent = contentFor(column.item, language);
    const content = column.markdownDraft ?? originalContent;
    const saveKey = `${slug}:${language}`;
    if (!slug || content === originalContent || savingMarkdownSlug === saveKey) {
      return;
    }
    setSavingMarkdownSlug(saveKey);
    setError("");
    try {
      const response = await fetch("/api/plot-tropes/browse/content", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, content, language })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      const item = payload?.item as TropeItem | undefined;
      if (!item) {
        throw new Error("saved trope row missing");
      }
      const savedContent = contentFor(item, language);
      setSelected((current) => (current?.slug === item.slug ? item : current));
      setContentColumns((current) =>
        current.map((currentColumn, index) => {
          if (currentColumn.mode === "markdown" && index === columnIndex) {
            return {
              ...currentColumn,
              slug: item.slug,
              sourceSlug: item.slug,
              item,
              markdownDraft: savedContent
            };
          }
          if (currentColumn.item?.slug === item.slug) {
            return {
              ...currentColumn,
              slug: item.slug,
              item
            };
          }
          return currentColumn;
        })
      );
      setLists((current) => {
        const itemType = normalizeTropeType(item.type);
        const existing = current[itemType] || [];
        return {
          ...current,
          [itemType]: existing.map((listItem) =>
            listItem.slug === item.slug
              ? {
                  ...listItem,
                  title: item.title,
                  type: item.type,
                  state: item.state,
                  hasIssue: item.hasIssue,
                  contentChars: item.contentChars,
                  updatedTs: item.updatedTs
                }
              : listItem
          )
        };
      });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "markdown save failed");
    } finally {
      setSavingMarkdownSlug((current) => (current === saveKey ? "" : current));
    }
  }, [contentColumns, contentLanguage, savingMarkdownSlug]);

  const captureContentScrollRatio = useCallback(() => {
    if (isMobileTrope && mobileStage === "detail") {
      const metrics = getWindowScrollMetrics();
      const maxTop = Math.max(0, metrics.scrollHeight - metrics.clientHeight);
      pendingContentScrollRatioRef.current = maxTop > 0 ? metrics.scrollTop / maxTop : 0;
      return;
    }
    const element = contentScrollRef.current;
    if (!element) {
      pendingContentScrollRatioRef.current = null;
      return;
    }
    const maxTop = Math.max(0, element.scrollHeight - element.clientHeight);
    pendingContentScrollRatioRef.current = maxTop > 0 ? element.scrollTop / maxTop : 0;
  }, [isMobileTrope, mobileStage]);

  const switchContentLanguage = useCallback(
    (language: TropeContentLanguage) => {
      if (language === contentLanguage) {
        return;
      }
      captureContentScrollRatio();
      persistTropeContentLanguage(language);
      setContentLanguage(language);
    },
    [captureContentScrollRatio, contentLanguage]
  );

  const toggleContentLanguage = useCallback(() => {
    switchContentLanguage(contentLanguage === "en" ? "ko" : "en");
  }, [contentLanguage, switchContentLanguage]);

  const handleListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget;
      if (element.scrollTop < 420) {
        loadPreviousActiveList();
      }
      if (element.scrollHeight - element.scrollTop - element.clientHeight < 420) {
        loadMoreActiveList();
      }
    },
    [loadMoreActiveList, loadPreviousActiveList]
  );

  useEffect(() => {
    if (!isMobileTrope || mobileStage !== "list") {
      return;
    }

    function handleWindowListScroll() {
      const metrics = getWindowScrollMetrics();
      if (metrics.scrollTop < 420) {
        loadPreviousActiveList();
      }
      if (metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight < 420) {
        loadMoreActiveList();
      }
    }

    window.addEventListener("scroll", handleWindowListScroll, { passive: true });
    handleWindowListScroll();
    return () => window.removeEventListener("scroll", handleWindowListScroll);
  }, [isMobileTrope, loadMoreActiveList, loadPreviousActiveList, mobileStage]);

  useLayoutEffect(() => {
    const pending = pendingPrependScrollRef.current;
    const listElement = listScrollRef.current;
    if (!pending || pending.type !== activeType || (!listElement && !(isMobileTrope && mobileStage === "list"))) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (isMobileTrope && mobileStage === "list") {
        const metrics = getWindowScrollMetrics();
        const delta = metrics.scrollHeight - pending.scrollHeight;
        window.scrollTo({ top: pending.scrollTop + Math.max(0, delta), behavior: "auto" });
      } else if (listElement) {
        const delta = listElement.scrollHeight - pending.scrollHeight;
        listElement.scrollTop = pending.scrollTop + Math.max(0, delta);
      }
      pendingPrependScrollRef.current = null;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeList, activeType, isMobileTrope, mobileStage]);

  useLayoutEffect(() => {
    const selectedSlug = selected?.slug || "";
    const selectedType = normalizeTropeType(selected?.type);
    if (!selectedSlug || selectedType !== activeType || pendingListCenterSlugRef.current !== selectedSlug) {
      return;
    }

    const listElement = listScrollRef.current;
    const itemElement = activeListItemRef.current;
    if (!listElement || !itemElement || !listElement.contains(itemElement)) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const listRect = listElement.getBoundingClientRect();
      const itemRect = itemElement.getBoundingClientRect();
      if (listRect.height <= 0 || itemRect.height <= 0) {
        return;
      }
      const targetTop =
        listElement.scrollTop +
        itemRect.top -
        listRect.top -
        Math.max(0, (listElement.clientHeight - itemElement.offsetHeight) / 2);
      const maxTop = Math.max(0, listElement.scrollHeight - listElement.clientHeight);
      listElement.scrollTo({
        top: Math.min(maxTop, Math.max(0, targetTop)),
        behavior: "auto"
      });
      pendingListCenterSlugRef.current = "";
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeList, activeType, selected?.slug, selected?.type]);

  useLayoutEffect(() => {
    const ratio = pendingContentScrollRatioRef.current;
    const element = contentScrollRef.current;
    if (ratio === null || (!element && !(isMobileTrope && mobileStage === "detail"))) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (isMobileTrope && mobileStage === "detail") {
        const metrics = getWindowScrollMetrics();
        const maxTop = Math.max(0, metrics.scrollHeight - metrics.clientHeight);
        window.scrollTo({ top: Math.min(maxTop, Math.max(0, ratio * maxTop)), behavior: "auto" });
      } else if (element) {
        const maxTop = Math.max(0, element.scrollHeight - element.clientHeight);
        element.scrollTop = Math.min(maxTop, Math.max(0, ratio * maxTop));
      }
      pendingContentScrollRatioRef.current = null;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [contentLanguage, isMobileTrope, mobileStage, selected?.slug]);

  useEffect(() => {
    if (initializedRouteRef.current) {
      return;
    }
    initializedRouteRef.current = true;
    const route = currentTropeRoute();
    desktopAutoOpenRef.current = !route.explicit;
    const fallbackLanguage = route.explicitLanguages ? route.paneLanguages[0] || "en" : storedTropeContentLanguage();
    const routePanes = routePanesFromRoute(route, fallbackLanguage);
    window.history.replaceState(
      tropeRouteState(route.type, route.slug, routePanes),
      "",
      route.explicit ? tropeRouteUrl(route.type, route.slug, routePanes) : window.location.href
    );
    void restoreTropeRoute(route);
  }, [restoreTropeRoute, routePanesFromRoute]);

  useEffect(() => {
    if (suppressAutoListLoadRef.current) {
      return;
    }
    if (!loadedTypes[activeType] && loadingListTypeRef.current !== activeType) {
      void loadList(activeType);
    }
  }, [activeType, loadList, loadedTypes, loadingListType]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px), (hover: none) and (pointer: coarse)");

    function syncMobileMode() {
      const matches = query.matches || document.documentElement.dataset.mobileRequest === "true";
      setIsMobileTrope(matches);
      if (!matches) {
        setMobileStage("detail");
      } else if (!selected) {
        setMobileStage("list");
      }
    }

    syncMobileMode();
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", syncMobileMode);
      return () => query.removeEventListener("change", syncMobileMode);
    }
    query.addListener(syncMobileMode);
    return () => query.removeListener(syncMobileMode);
  }, [selected]);

  useEffect(() => {
    if (!isMobileTrope) {
      delete document.documentElement.dataset.tropeMobileStage;
      return;
    }
    document.documentElement.dataset.tropeMobileStage = mobileStage;
    return () => {
      delete document.documentElement.dataset.tropeMobileStage;
    };
  }, [isMobileTrope, mobileStage]);

  useLayoutEffect(() => {
    if (!isMobileTrope) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      if (mobileStage === "detail") {
        window.scrollTo({ top: 0, behavior: "auto" });
      } else {
        window.scrollTo({ top: mobileListScrollTopRef.current, behavior: "auto" });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isMobileTrope, mobileStage, selected?.slug]);

  useEffect(() => {
    const selectedType = normalizeTropeType(selected?.type);
    if (selected && selectedType === activeType) {
      return;
    }
    if (!isMobileTrope && activeList.length > 0 && !loadingSlug && desktopAutoOpenRef.current) {
      desktopAutoOpenRef.current = false;
      void openSlug(activeList[0].slug, "replace");
    }
  }, [activeList, activeType, isMobileTrope, loadingSlug, openSlug, selected]);

  useEffect(() => {
    function handlePopState() {
      const route = currentTropeRoute();
      desktopAutoOpenRef.current = false;
      void restoreTropeRoute(route);
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [restoreTropeRoute]);

  useEffect(() => {
    if (!initializedRouteRef.current || !selected || mobileStage === "list") {
      return;
    }
    const panes = routePanesForColumns();
    if (panes.length === 0) {
      return;
    }
    writeTropeRoute(normalizeTropeType(selected.type), panes[0].slug, "replace", panes);
  }, [contentColumns, contentLanguage, mobileStage, routePanesForColumns, selected, writeTropeRoute]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey ||
        isTextEditingTarget(event.target)
      ) {
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        desktopAutoOpenRef.current = false;
        void moveSelectedInList(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        desktopAutoOpenRef.current = false;
        void moveSelectedInList(-1);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [moveSelectedInList]);

  function switchType(type: TropeType) {
    desktopAutoOpenRef.current = false;
    clearSearchState();
    showListRoute(type);
  }

  function navigateBackToList() {
    desktopAutoOpenRef.current = false;
    showListRoute(activeType);
  }

  function renderTropeTypeControl(item: Pick<TropeItem, "slug" | "type"> | null | undefined, scope: "selected" | "column" = "selected") {
    if (!item) {
      return null;
    }
    const currentType = normalizeTropeType(item.type);
    const label = tropeTypeLabel(currentType);
    const glyph = tropeTypeGlyph(currentType);
    const options = isMobileTrope ? [] : switchableTropeTypeOptions(currentType);
    const isSaving = savingTypeSlug === item.slug;
    if (options.length === 0) {
      return (
        <span aria-label={label} className="tropeEdgeDebugText" title={label}>
          {glyph}
        </span>
      );
    }
    return (
      <span className="tropeEdgeDebugCluster">
        <button
          aria-label={`${label} type actions`}
          className="tropeEdgeDebugButton"
          disabled={isSaving}
          title={label}
          type="button"
        >
          {isSaving ? "..." : glyph}
        </button>
        <span className="tropeEdgeDebugMenu">
          {options.map((nextType) => {
            const nextLabel = tropeTypeLabel(nextType);
            return (
              <button
                aria-label={`Set as ${nextLabel}`}
                className="tropeEdgeDebugAction"
                disabled={isSaving}
                key={nextType}
                onClick={() => switchTropeType(item, nextType, scope)}
                title={`Set as ${nextLabel}`}
                type="button"
              >
                {tropeTypeGlyph(nextType)}
              </button>
            );
          })}
        </span>
      </span>
    );
  }

  function renderLanguageCompareButton(slug: string, sourceColumnIndex: number, language: TropeContentLanguage) {
    const cleanSlug = cleanTropeSlug(slug);
    const nextLanguage = oppositeLanguage(language);
    const label = `Compare with ${languageLabel(nextLanguage)}`;
    return (
      <button
        aria-label={label}
        className="tropeLanguageCompareButton"
        disabled={!cleanSlug}
        onClick={() => void openColumnSlug(cleanSlug, sourceColumnIndex, undefined, nextLanguage)}
        title={label}
        type="button"
      >
        <Languages size={15} />
      </button>
    );
  }

  function renderRetranslateButton(item: TropeItem | null | undefined) {
    if (!item) {
      return null;
    }
    const cleanSlug = cleanTropeSlug(item.slug);
    const stateKo = Number(item.stateKo || 0);
    const isSaving = savingRetranslateSlug === cleanSlug;
    const queued = stateKo === 5;
    const translating = stateKo === 1;
    const disabled = !cleanSlug || isSaving || queued || translating;
    const title = translating ? "번역중" : queued ? "우선 번역 대기" : "재번역";
    return (
      <button
        aria-label={title}
        className="tropeLanguageCompareButton tropeRetranslateButton"
        disabled={disabled}
        onClick={() => void enqueueRetranslation(item)}
        title={title}
        type="button"
      >
        {isSaving ? <Loader2 size={15} /> : <RefreshCw size={15} />}
      </button>
    );
  }

  function renderLanguageActionCluster(item: TropeItem | null | undefined, slug: string, sourceColumnIndex: number, language: TropeContentLanguage) {
    return (
      <span className="tropeLanguageActionCluster">
        {renderRetranslateButton(item)}
        {renderLanguageCompareButton(slug, sourceColumnIndex, language)}
      </span>
    );
  }

  function renderMarkdownColumnButton(sourceColumnIndex: number, item: TropeItem | null | undefined, language: TropeContentLanguage) {
    if (!item || isMobileTrope) {
      return null;
    }
    const cleanSlug = cleanTropeSlug(item.slug);
    const nextColumn = contentColumns[sourceColumnIndex + 1];
    const isOpen = Boolean(nextColumn?.mode === "markdown" && nextColumn.sourceSlug === cleanSlug && nextColumn.language === language);
    return (
      <button
        aria-label="Markdown"
        aria-pressed={isOpen}
        className={`tropeMarkdownToggle iconOnly ${isOpen ? "active" : ""}`}
        onClick={() => openMarkdownColumn(sourceColumnIndex)}
        title="markdown"
        type="button"
      >
        <FileText size={15} />
      </button>
    );
  }

  function renderColumnHeader(item: TropeItem | null, slug: string, columnIndex: number, language: TropeContentLanguage) {
    if (columnIndex === 0) {
      return null;
    }
    const title = item ? translatedLabelFor(item, language) : slug;
    const columnSlug = item?.slug || slug;
    return (
      <div className="tropeContentColumnHeader">
        <div className="tropeContentColumnTitleRow">
          <h3>{title}</h3>
          {item ? renderTropeTypeControl(item, "column") : null}
          {renderMarkdownColumnButton(columnIndex, item, language)}
        </div>
        <div className="tropeContentColumnMetaRow">
          <span className="tropeHeaderSlug mono">{columnSlug}</span>
          {renderLanguageActionCluster(item, columnSlug, columnIndex, language)}
        </div>
        {item?.ancestorTags && item.ancestorTags.length > 0 ? (
          <div className="tropeIndexTagRow tropeContentColumnTags" aria-label="Column index tags">
            {item.ancestorTags.map((tag) => {
              const fullTagLabel = translatedLabelFor(tag, language);
              const tagLabel = stripIndexTagTitle(fullTagLabel);
              return (
                <button
                  className={`tropeIndexTag depth${Math.min(4, Math.max(1, Number(tag.depth || 1)))}`}
                  key={`${tag.id}-${tag.slug}`}
                  onClick={(event) => openColumnSlug(tag.slug, columnIndex, event)}
                  title={`${fullTagLabel} (${tag.slug})`}
                  type="button"
                >
                  {tagLabel}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }

  function renderMarkdownColumn(column: TropeContentColumn, columnIndex: number) {
    const item = column.item;
    const language = column.language || contentLanguage;
    const sourceSlug = cleanTropeSlug(column.sourceSlug || item?.slug || column.slug || "");
    const sourceContent = item ? contentFor(item, language) : "";
    const draft = column.markdownDraft ?? sourceContent;
    const dirty = Boolean(item && draft !== sourceContent);
    const saveKey = `${sourceSlug}:${language}`;
    const saving = savingMarkdownSlug === saveKey;
    const title = item ? translatedLabelFor(item, language) : sourceSlug || "Markdown";
    return (
      <section
        aria-label={`Raw markdown column ${columnIndex + 1}`}
        className="tropeContentColumn markdown"
        key={`${columnIndex}-${sourceSlug || "markdown"}-${language}-markdown`}
      >
        <div className="tropeContentColumnHeader">
          <div className="tropeContentColumnTitleRow">
            <h3>Markdown</h3>
          </div>
          <div className="tropeContentColumnMetaRow">
            <span className="tropeHeaderSlug mono" title={sourceSlug}>
              {sourceSlug}
            </span>
            <span className="tropeMarkdownColumnSource" title={title}>
              {title}
            </span>
          </div>
        </div>
        <textarea
          aria-label="Raw markdown"
          className="tropeMarkdownScroll"
          onChange={(event) => updateMarkdownColumnDraft(columnIndex, event.currentTarget.value)}
          ref={(element) => {
            if (element) {
              markdownColumnRefs.current.set(columnIndex, element);
            } else {
              markdownColumnRefs.current.delete(columnIndex);
            }
          }}
          spellCheck={false}
          value={draft}
        />
        <div className="tropeMarkdownFloatingTools" aria-label="Markdown tools">
          <button aria-label="Scroll markdown down" onClick={() => scrollMarkdownColumnDown(columnIndex)} title="down" type="button">
            <ArrowDown size={15} />
          </button>
          <button aria-label="Copy markdown" onClick={() => void copyTextToClipboard(draft)} title="copy" type="button">
            <Copy size={15} />
          </button>
          <button
            aria-label="Save markdown"
            disabled={!dirty || saving}
            onClick={() => void saveMarkdownColumn(columnIndex)}
            title="save"
            type="button"
          >
            {saving ? <Loader2 size={15} /> : <Save size={15} />}
          </button>
        </div>
      </section>
    );
  }

  function renderContentColumn(column: TropeContentColumn, columnIndex: number) {
    if (column.mode === "markdown") {
      return renderMarkdownColumn(column, columnIndex);
    }
    const isPrimaryColumn = columnIndex === 0;
    const item = isPrimaryColumn ? selected : column.item;
    const columnLanguage = isPrimaryColumn ? contentLanguage : column.language || contentLanguage;
    const displayContent = contentFor(item, columnLanguage);
    const isLoading = isPrimaryColumn ? Boolean(loadingSlug) : column.loading;
    const loadingLabel = isPrimaryColumn ? loadingSlug : column.slug;
    const columnError = isPrimaryColumn ? "" : column.error;
    const canEditEdges = Boolean(item && !isMobileTrope && (item.type === 2 || item.type === 3));
    return (
      <section
        aria-label={isPrimaryColumn ? "Selected trope content" : `Opened trope column ${columnIndex + 1}`}
        className={`tropeContentColumn ${isPrimaryColumn ? "primary" : "secondary"}`}
        key={`${columnIndex}-${column.slug || item?.slug || "empty"}-${columnLanguage}`}
      >
        {renderColumnHeader(item, column.slug, columnIndex, columnLanguage)}
        <div className="tropeContentColumnScroll" ref={isPrimaryColumn ? contentScrollRef : undefined}>
          {isLoading ? (
            <div className="tropeLoading">
              <Loader2 size={18} />
              Loading {loadingLabel}
            </div>
          ) : columnError ? (
            <div className="tropeError inline">{columnError}</div>
          ) : item && displayContent ? (
            <MarkdownView
              markdown={displayContent}
              onAssignEdgeList={isPrimaryColumn ? assignEdgeList : undefined}
              onAssignEdge={canEditEdges ? (childSlug) => updateSingleEdgeForParent(item, columnLanguage, childSlug, "add") : undefined}
              onInternalLink={handleColumnInternalLink(columnIndex)}
              onRemoveEdge={canEditEdges ? (childSlug) => updateSingleEdgeForParent(item, columnLanguage, childSlug, "delete") : undefined}
              onSetEdgeType={canEditEdges ? (childSlug, type) => setSingleEdgeType(childSlug, type, item) : undefined}
              canAssignEdges={canEditEdges}
              forceMediaEdgeTools={canEditEdges && item.type === 3}
              edgeDebug={item.edgeDebug}
              readOnlyEdgeDebug={isMobileTrope}
              fixingFloatbox={isPrimaryColumn && fixingFloatboxSlug === item.slug}
              onFixFloatbox={isPrimaryColumn ? fixSelectedFloatbox : undefined}
              savingEdgeListKey={isPrimaryColumn ? savingEdgeListKey : ""}
              savingEdgeSlug={isPrimaryColumn ? savingEdgeSlug : ""}
            />
          ) : item ? (
            <div className="tropeEmptyContent">
              <FileText size={20} />
              Content is not parsed yet.
            </div>
          ) : (
            <div className="tropeEmptyContent">
              <FileText size={20} />
              Select an item from the list.
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <main className={`tropeWorkspace tropeMobileStage-${mobileStage}`}>
      <section className="tropeListPane" aria-label="Trope list">
        <div className="tropeListHeader">
          {searchMode ? (
            <form
              className="tropeSearchForm"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
            >
              <button
                aria-label="Cancel search"
                className="tropeSearchIconButton"
                onClick={clearSearchState}
                title="Cancel search"
                type="button"
              >
                <X size={16} />
              </button>
              <input
                className="tropeSearchInput"
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={`Search ${tropeTypeLabel(activeType)}`}
                ref={searchInputRef}
                type="search"
                value={searchText}
              />
              <button
                aria-label="Search"
                className="tropeSearchIconButton submit"
                disabled={loadingSearch || !searchText.trim()}
                title="Search"
                type="submit"
              >
                {loadingSearch ? <Loader2 size={16} /> : <Search size={16} />}
              </button>
            </form>
          ) : (
            <div className="tropeHeaderTabsRow">
              <div className="tropeTypeTabs" role="tablist" aria-label="Trope type">
                {tabs.map((tab) => (
                  <button
                    className={`tropeTypeTab ${activeType === tab.type ? "active" : ""}`}
                    key={tab.type}
                    onClick={() => switchType(tab.type)}
                    role="tab"
                    type="button"
                  >
                    <span className="tropeTypeTabGlyph" aria-hidden="true">{tropeTypeGlyph(tab.type)}</span>
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                aria-label="Search"
                className="tropeSearchOpenButton"
                onClick={() => {
                  setSearchMode(true);
                  setSearchText(searchQuery);
                }}
                title="Search"
                type="button"
              >
                <Search size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="tropeListMeta">
          {isActiveListLoading && activeList.length === 0
            ? "loading"
            : isSearchResultMode
              ? `${activeList.length.toLocaleString()} / ${activeListMeta.total.toLocaleString()} matches for "${searchQuery}"`
              : `${activeList.length.toLocaleString()} / ${activeListMeta.total.toLocaleString()} items`}
        </div>

        <div className="tropeList" onScroll={handleListScroll} ref={listScrollRef}>
          {activeListMeta.hasBefore ? (
            <div className="tropeListMore top">
              {isActiveListLoading ? (
                <span className="tropeListMoreStatus">
                  <Loader2 size={14} />
                  loading
                </span>
              ) : (
                <button onClick={loadPreviousActiveList} type="button">
                  Load previous
                </button>
              )}
            </div>
          ) : null}
          {activeList.map((item) => {
            const active = selected?.slug === item.slug;
            const itemLabel = stripIndexTagTitle(translatedLabelFor(item, contentLanguage));
            return (
              <button
                className={`tropeListItem ${active ? "active" : ""}`}
                data-slug={item.slug}
                key={item.slug}
                onClick={() => openSlug(item.slug)}
                ref={active ? (element) => {
                  activeListItemRef.current = element;
                } : undefined}
                type="button"
              >
                <span className="tropeListItemTitle">{itemLabel}</span>
                {item.pinned ? (
                  <span className="tropeListPinnedAccessory" aria-label="Pinned" title="Pinned">
                    <Bookmark size={12} />
                  </span>
                ) : null}
              </button>
            );
          })}
          {!isSearchResultMode && loadedTypes[activeType] && activeList.length === 0 ? (
            <div className="tropeEmpty">No rows for this type.</div>
          ) : null}
          {isSearchResultMode && !loadingSearch && activeList.length === 0 ? (
            <div className="tropeEmpty">No matches.</div>
          ) : null}
          {activeListMeta.hasMore ? (
            <div className="tropeListMore">
              {isActiveListLoading ? (
                <span className="tropeListMoreStatus">
                  <Loader2 size={14} />
                  loading
                </span>
              ) : (
                <button onClick={loadMoreActiveList} type="button">
                  Load more
                </button>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <section className="tropeContentPane" aria-label="Trope content" ref={contentPaneRef}>
        <div className="tropeContentHeader">
          <div className="tropeContentHeaderBody">
            <div className="tropeContentTitleRow">
              <button
                aria-label={selected?.pinned ? "Unpin trope" : "Pin trope"}
                aria-pressed={Boolean(selected?.pinned)}
                className={`tropePinnedButton ${selected?.pinned ? "active" : ""}`}
                disabled={!selected || savingPinnedSlug === selected.slug}
                onClick={toggleSelectedPinned}
                title={selected?.pinned ? "Unpin" : "Pin"}
                type="button"
              >
                <Bookmark size={18} />
              </button>
              <h2>{selected ? translatedLabelFor(selected, contentLanguage) : "Select a trope"}</h2>
              {selected ? <span className="tropeHeaderSlug mono">{selected.slug}</span> : null}
              {selected ? renderTropeTypeControl(selected, "selected") : null}
            </div>
            {selected && selected.ancestorTags && selected.ancestorTags.length > 0 ? (
              <div className="tropeIndexTagRow" aria-label="Index tags">
                {selected.ancestorTags.map((tag) => {
                  const fullTagLabel = translatedLabelFor(tag, contentLanguage);
                  const tagLabel = stripIndexTagTitle(fullTagLabel);
                  return (
                    <button
                      className={`tropeIndexTag depth${Math.min(4, Math.max(1, Number(tag.depth || 1)))}`}
                      key={`${tag.id}-${tag.slug}`}
                      onClick={(event) => openColumnSlug(tag.slug, 0, event)}
                      title={`${fullTagLabel} (${tag.slug})`}
                      type="button"
                    >
                      {tagLabel}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          {selected ? (
            <div className="tropeHeaderActions">
              <div className="tropeHeaderActionRow">
                {renderMarkdownColumnButton(0, selected, contentLanguage)}
                <a className="tropeSourceLink" href={tropeSourceUrl(selected.slug)} rel="noreferrer" target="_blank">
                  URL
                </a>
              </div>
              <div className="tropeLanguageCompareRow">
                <div className="tropeLanguageTabs" role="tablist" aria-label="Content language">
                  <button
                    aria-selected={contentLanguage === "en"}
                    className={`tropeLanguageTab ${contentLanguage === "en" ? "active" : ""}`}
                    onClick={() => switchContentLanguage("en")}
                    role="tab"
                    type="button"
                  >
                    Eng
                  </button>
                  <button
                    aria-selected={contentLanguage === "ko"}
                    className={`tropeLanguageTab ${contentLanguage === "ko" ? "active" : ""}`}
                    onClick={() => switchContentLanguage("ko")}
                    role="tab"
                    type="button"
                  >
                    한글
                  </button>
                </div>
                {renderLanguageActionCluster(selected, selected.slug, 0, contentLanguage)}
              </div>
            </div>
          ) : null}
        </div>

        {error ? <div className="tropeError">{error}</div> : null}

        <div
          className="tropeContentBody"
          ref={contentBodyRef}
        >
          <div className={`tropeColumnHScroll ${columnLayoutClass}`} ref={contentColumnHScrollRef}>
            {visibleContentColumns.map((column, columnIndex) => renderContentColumn(column, columnIndex))}
          </div>
        </div>
      </section>
      {isMobileTrope && mobileStage === "detail" ? (
        <button className="tropeMobileBack" onClick={navigateBackToList} type="button" aria-label="Back to trope list">
          <ChevronLeft size={18} />
          <span>Tropes</span>
        </button>
      ) : null}
      {isMobileTrope ? (
        <div className="tropeMobileNavStack" aria-label="Mobile trope navigation">
          <button
            aria-label={contentLanguage === "en" ? "Switch to Korean" : "Switch to English"}
            className="tropeMobileLanguageToggle"
            onClick={toggleContentLanguage}
            title={contentLanguage === "en" ? "Switch to Korean" : "Switch to English"}
            type="button"
          >
            {contentLanguage === "en" ? "Eng" : "한글"}
          </button>
          {mobileStage === "detail" && selected ? (
            <>
              <button
                aria-label="Previous trope"
                className="tropeMobileStepButton"
                disabled={Boolean(loadingSlug) || loadingListType === activeType}
                onClick={() => void moveSelectedInList(-1)}
                title="Previous trope"
                type="button"
              >
                <ArrowUp size={18} />
              </button>
              <button
                aria-label="Next trope"
                className="tropeMobileStepButton"
                disabled={Boolean(loadingSlug) || loadingListType === activeType}
                onClick={() => void moveSelectedInList(1)}
                title="Next trope"
                type="button"
              >
                <ArrowDown size={18} />
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </main>
  );
}
